import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertCircle, ArrowLeft, ArrowRight, CheckCircle2,
  ChevronDown, ClipboardList, Loader2, Minus, User,
  Clock, Eye, EyeOff, Download, MessageSquare, FileText, Plus, Trash2,
  Camera, X, Info, Pencil,
} from 'lucide-react';
import confetti from 'canvas-confetti';
import axios from 'axios';

import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getToken } from '../services/authToken';
import useReducedMotion from '../hooks/useReducedMotion';
import {
  useCabinCVCurrent,
  useSaveCVDraft,
  useSubmitCV,
  useUploadCvPhoto,
  useDeleteCvPhoto,
} from '../hooks/cabin/useCabinCV';
import WizardSteps from '../components/ui/WizardSteps';
import CabinErrorState from '../components/cabin/CabinErrorState';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { buildEuropassBody } from '../utils/buildEuropassBody';
import { seedSelectionState } from '../utils/cvSelectionState';
import { CVCanvas, TEMPLATE_IDS } from '../components/cv/CVCanvasPreview';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────────────
// Validation schemas (Zod)
// ─────────────────────────────────────────────────────────────────────────────

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const phoneRegex = /^\+90[0-9]{10}$/;

const step1Schema = z.object({
  ad: z.string().min(1, 'cv.validation.ad_required'),
  soyad: z.string().min(1, 'cv.validation.soyad_required'),
  dogum_tarihi: z.string()
    .min(1, 'cv.validation.dogum_required')
    .refine((v) => {
      const d = new Date(v);
      if (isNaN(d.getTime())) return false;
      const today = new Date();
      const age = today.getFullYear() - d.getFullYear();
      return age >= 18 && age <= 40;
    }, 'cv.validation.dogum_age'),
  telefon: z.string()
    .min(1, 'cv.validation.telefon_required')
    .regex(phoneRegex, 'cv.validation.telefon_format'),
  eposta: z.string()
    .min(1, 'cv.validation.eposta_required')
    .regex(emailRegex, 'cv.validation.eposta_format'),
  cinsiyet: z.string().optional(),
  boy_cm: z.coerce.number().min(140, 'cv.validation.boy_min').max(210, 'cv.validation.boy_max').optional().or(z.literal('')),
  kilo_kg: z.coerce.number().min(40, 'cv.validation.kilo_min').max(150, 'cv.validation.kilo_max').optional().or(z.literal('')),
  sehir: z.string().optional(),
  pasaport_var: z.boolean().optional(),
  seyahat_engeli: z.boolean().optional(),
  seyahat_engeli_aciklama: z.string().optional(),
  adli_sicil: z.boolean().optional(),
  adli_sicil_aciklama: z.string().optional(),
  onceki_havayolu_basvurusu: z.boolean().optional(),
  mulakate_girdi_mi: z.boolean().optional(),
  elendigi_asama: z.string().optional(),
  instagram: z.string().optional(),
  linkedin: z.string().optional(),
});

const step2Schema = z.object({
  mezuniyet_durumu: z.string().optional(),
  okul: z.string().optional(),
  bolum: z.string().optional(),
  mezuniyet_yili: z.coerce.number().min(1980, 'cv.validation.yil_min').max(new Date().getFullYear() + 6, 'cv.validation.yil_max').optional().or(z.literal('')),
  devam_eden_egitim: z.boolean().optional(),
  yabanci_dil_egitimi: z.string().optional(),
  kabin_memurlugu_egitimi: z.boolean().optional(),
  ilk_yardim_egitimi: z.boolean().optional(),
  diksiyon_egitimi: z.boolean().optional(),
});

const step4Schema = z.object({
  ingilizce_seviye: z.string().optional(),
  konusma_pratigi: z.string().optional(),
  ingilizce_mulakat_tecrubesi: z.string().optional(),
});

const step6Schema = z.object({
  unvan: z.string().optional(),
  hakkimda: z.string().max(600, 'cv.validation.hakkimda_max').optional(),
});

// Europass 3-adım: Welcome → Düzenle → Kaydet
const emptySchema = z.object({});
const EUROPASS_STEP_SCHEMAS = [
  emptySchema, // 0: Welcome
  emptySchema, // 1: Düzenle
  emptySchema, // 2: Kaydet
];

// ─────────────────────────────────────────────────────────────────────────────
// State reducer
// ─────────────────────────────────────────────────────────────────────────────

const AUTOSAVE_IDLE = 'idle';
const AUTOSAVE_SAVING = 'saving';
const AUTOSAVE_SAVED = 'saved';
const AUTOSAVE_ERROR = 'error';

function wizardReducer(state, action) {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.step };
    case 'MARK_COMPLETED':
      return { ...state, completed: new Set([...state.completed, action.step]) };
    case 'MARK_SKIPPED':
      return {
        ...state,
        skipped: new Set([...state.skipped, action.step]),
        completed: (() => {
          const c = new Set(state.completed);
          c.delete(action.step);
          return c;
        })(),
      };
    case 'SET_AUTOSAVE':
      return { ...state, autosaveStatus: action.status };
    case 'SET_KVKK':
      return { ...state, kvkkConsented: action.value };
    case 'SET_SUBMITTED':
      return { ...state, submitted: true };
    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

// seedSelectionState — moved to src/utils/cvSelectionState.js (imported above)

/**
 * Mevcut CV verisinde kaç bölümün dolu olduğunu sayar.
 * Welcome adımındaki profil özet kartında kullanılır.
 */
function countFilledSections(cv) {
  if (!cv) return 0;
  let n = 0;
  if (cv.kisisel && (cv.kisisel.ad_soyad || cv.kisisel.telefon)) n++;
  if (cv.egitim && (cv.egitim.okul || cv.egitim.mezuniyet_durumu)) n++;
  if (cv.is_deneyimi && cv.is_deneyimi.calistimi != null) n++;
  if (cv.dil && (cv.dil.ingilizce_seviye || (cv.dil.diller && cv.dil.diller.length > 0))) n++;
  if (cv.motivasyon && cv.motivasyon.neden_kabin_memuru) n++;
  return n;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

/** Reusable field wrapper with label and error message */
function Field({ id, label, error, required, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={id}
        className="text-xs font-bold text-gray-700 uppercase tracking-wide"
      >
        {label}
        {required && <span className="text-rose-500 ml-0.5" aria-hidden="true">*</span>}
      </label>
      {children}
      {error && (
        <p
          id={`${id}-error`}
          role="alert"
          aria-live="polite"
          className="flex items-center gap-1 text-xs text-rose-600 font-medium"
        >
          <AlertCircle size={12} aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}

/** Reusable text input */
function TextInput({ id, error, ...props }) {
  return (
    <input
      id={id}
      aria-invalid={!!error}
      aria-describedby={error ? `${id}-error` : undefined}
      className={`w-full px-3 py-2.5 rounded-xl border-2 text-sm font-medium bg-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-1 min-h-[44px]
        ${error
          ? 'border-rose-400 focus:border-rose-500'
          : 'border-gray-200 focus:border-sky-400 hover:border-gray-300'
        }`}
      {...props}
    />
  );
}

/** Reusable select */
function SelectInput({ id, error, children, ...props }) {
  return (
    <select
      id={id}
      aria-invalid={!!error}
      aria-describedby={error ? `${id}-error` : undefined}
      className={`w-full px-3 py-2.5 rounded-xl border-2 text-sm font-medium bg-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-1 min-h-[44px]
        ${error
          ? 'border-rose-400 focus:border-rose-500'
          : 'border-gray-200 focus:border-sky-400'
        }`}
      {...props}
    >
      {children}
    </select>
  );
}

/** Reusable textarea */
function TextAreaInput({ id, error, ...props }) {
  return (
    <textarea
      id={id}
      aria-invalid={!!error}
      aria-describedby={error ? `${id}-error` : undefined}
      className={`w-full px-3 py-2.5 rounded-xl border-2 text-sm font-medium bg-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-1 resize-none
        ${error
          ? 'border-rose-400 focus:border-rose-500'
          : 'border-gray-200 focus:border-sky-400'
        }`}
      rows={3}
      {...props}
    />
  );
}

/** Checkbox with label */
function CheckboxField({ id, label, checked, onChange, className = '' }) {
  return (
    <label
      htmlFor={id}
      className={`flex items-center gap-2.5 cursor-pointer select-none min-h-[44px] ${className}`}
    >
      <input
        type="checkbox"
        id={id}
        checked={!!checked}
        onChange={onChange}
        className="w-5 h-5 rounded-md border-2 border-gray-300 accent-sky-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 flex-shrink-0"
      />
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Autosave Indicator
// ─────────────────────────────────────────────────────────────────────────────

function AutosaveIndicator({ status, t }) {
  if (status === AUTOSAVE_IDLE) return null;
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="flex items-center gap-1.5 text-xs font-medium"
    >
      {status === AUTOSAVE_SAVING && (
        <>
          <Loader2 size={13} className="text-sky-500 animate-spin" aria-hidden="true" />
          <span className="text-sky-600">{t('cv.autosave.saving')}</span>
        </>
      )}
      {status === AUTOSAVE_SAVED && (
        <>
          <CheckCircle2 size={13} className="text-emerald-500" aria-hidden="true" />
          <span className="text-emerald-600">{t('cv.autosave.saved')}</span>
        </>
      )}
      {status === AUTOSAVE_ERROR && (
        <>
          <AlertCircle size={13} className="text-rose-500" aria-hidden="true" />
          <span className="text-rose-600">{t('cv.autosave.error')}</span>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 1 — Kişisel Bilgiler
// ─────────────────────────────────────────────────────────────────────────────

function Step1Form({ form, t }) {
  const { register, formState: { errors }, watch } = form;
  const seyahatEngeli = watch('seyahat_engeli');
  const adliSicil = watch('adli_sicil');
  const oncekiBasvuru = watch('onceki_havayolu_basvurusu');
  const mulakateGirdi = watch('mulakate_girdi_mi');

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="cv-ad" label={t('cv.field.ad')} error={t(errors.ad?.message)} required>
          <TextInput
            id="cv-ad"
            placeholder={t('cv.placeholder.ad')}
            error={errors.ad}
            {...register('ad')}
          />
        </Field>
        <Field id="cv-soyad" label={t('cv.field.soyad')} error={t(errors.soyad?.message)} required>
          <TextInput
            id="cv-soyad"
            placeholder={t('cv.placeholder.soyad')}
            error={errors.soyad}
            {...register('soyad')}
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="cv-dogum" label={t('cv.field.dogum_tarihi')} error={t(errors.dogum_tarihi?.message)} required>
          <TextInput
            id="cv-dogum"
            type="date"
            error={errors.dogum_tarihi}
            {...register('dogum_tarihi')}
          />
        </Field>
        <Field id="cv-cinsiyet" label={t('cv.field.cinsiyet')}>
          <SelectInput id="cv-cinsiyet" {...register('cinsiyet')}>
            <option value="">{t('cv.placeholder.select')}</option>
            <option value="kadin">{t('cv.option.kadin')}</option>
            <option value="erkek">{t('cv.option.erkek')}</option>
            <option value="diger">{t('cv.option.diger')}</option>
          </SelectInput>
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="cv-telefon" label={t('cv.field.telefon')} error={t(errors.telefon?.message)} required>
          <TextInput
            id="cv-telefon"
            type="tel"
            placeholder="+905XXXXXXXXX"
            error={errors.telefon}
            {...register('telefon')}
          />
        </Field>
        <Field id="cv-eposta" label={t('cv.field.eposta')} error={t(errors.eposta?.message)} required>
          <TextInput
            id="cv-eposta"
            type="email"
            placeholder="ornek@email.com"
            error={errors.eposta}
            {...register('eposta')}
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field id="cv-boy" label={t('cv.field.boy_cm')} error={t(errors.boy_cm?.message)}>
          <TextInput
            id="cv-boy"
            type="number"
            min={140}
            max={210}
            placeholder="170"
            error={errors.boy_cm}
            {...register('boy_cm')}
          />
        </Field>
        <Field id="cv-kilo" label={t('cv.field.kilo_kg')} error={t(errors.kilo_kg?.message)}>
          <TextInput
            id="cv-kilo"
            type="number"
            min={40}
            max={150}
            placeholder="65"
            error={errors.kilo_kg}
            {...register('kilo_kg')}
          />
        </Field>
        <Field id="cv-sehir" label={t('cv.field.sehir')}>
          <TextInput
            id="cv-sehir"
            placeholder={t('cv.placeholder.sehir')}
            {...register('sehir')}
          />
        </Field>
      </div>

      {/* Pasaport & Seyahat */}
      <div className="bg-gray-50 rounded-xl border-2 border-gray-100 p-4 space-y-3">
        <h3 className="text-xs font-black text-gray-600 uppercase tracking-wide">
          {t('cv.section.pasaport_seyahat')}
        </h3>
        <CheckboxField
          id="cv-pasaport"
          label={t('cv.field.pasaport_var')}
          checked={form.watch('pasaport_var')}
          onChange={(e) => form.setValue('pasaport_var', e.target.checked)}
        />
        <CheckboxField
          id="cv-seyahat-engel"
          label={t('cv.field.seyahat_engeli')}
          checked={form.watch('seyahat_engeli')}
          onChange={(e) => form.setValue('seyahat_engeli', e.target.checked)}
        />
        {seyahatEngeli && (
          <Field id="cv-seyahat-aciklama" label={t('cv.field.seyahat_engeli_aciklama')}>
            <TextInput
              id="cv-seyahat-aciklama"
              placeholder={t('cv.placeholder.aciklama')}
              {...register('seyahat_engeli_aciklama')}
            />
          </Field>
        )}
      </div>

      {/* Adli Sicil */}
      <div className="bg-gray-50 rounded-xl border-2 border-gray-100 p-4 space-y-3">
        <h3 className="text-xs font-black text-gray-600 uppercase tracking-wide">
          {t('cv.section.adli_sicil')}
        </h3>
        <CheckboxField
          id="cv-adli-sicil"
          label={t('cv.field.adli_sicil')}
          checked={form.watch('adli_sicil')}
          onChange={(e) => form.setValue('adli_sicil', e.target.checked)}
        />
        {adliSicil && (
          <Field id="cv-adli-aciklama" label={t('cv.field.adli_sicil_aciklama')}>
            <TextInput
              id="cv-adli-aciklama"
              placeholder={t('cv.placeholder.aciklama')}
              {...register('adli_sicil_aciklama')}
            />
          </Field>
        )}
      </div>

      {/* Önceki Başvuru */}
      <div className="bg-gray-50 rounded-xl border-2 border-gray-100 p-4 space-y-3">
        <h3 className="text-xs font-black text-gray-600 uppercase tracking-wide">
          {t('cv.section.onceki_basvuru')}
        </h3>
        <CheckboxField
          id="cv-onceki-basvuru"
          label={t('cv.field.onceki_havayolu_basvurusu')}
          checked={form.watch('onceki_havayolu_basvurusu')}
          onChange={(e) => form.setValue('onceki_havayolu_basvurusu', e.target.checked)}
        />
        {oncekiBasvuru && (
          <>
            <CheckboxField
              id="cv-mulakat-girdi"
              label={t('cv.field.mulakate_girdi_mi')}
              checked={form.watch('mulakate_girdi_mi')}
              onChange={(e) => form.setValue('mulakate_girdi_mi', e.target.checked)}
            />
            {mulakateGirdi && (
              <Field id="cv-elendigi-asama" label={t('cv.field.elendigi_asama')}>
                <TextInput
                  id="cv-elendigi-asama"
                  placeholder={t('cv.placeholder.elendigi_asama')}
                  {...register('elendigi_asama')}
                />
              </Field>
            )}
          </>
        )}
      </div>

      {/* Sosyal Medya */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="cv-instagram" label={t('cv.field.instagram')}>
          <TextInput
            id="cv-instagram"
            placeholder="@kullanici_adi"
            {...register('instagram')}
          />
        </Field>
        <Field id="cv-linkedin" label={t('cv.field.linkedin')}>
          <TextInput
            id="cv-linkedin"
            placeholder="linkedin.com/in/..."
            {...register('linkedin')}
          />
        </Field>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 2 — Eğitim Bilgileri
// ─────────────────────────────────────────────────────────────────────────────

function Step2Form({ form, t }) {
  const { register, formState: { errors } } = form;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="cv-mezuniyet" label={t('cv.field.mezuniyet_durumu')}>
          <SelectInput id="cv-mezuniyet" {...register('mezuniyet_durumu')}>
            <option value="">{t('cv.placeholder.select')}</option>
            <option value="ortaogretim">{t('cv.option.ortaogretim')}</option>
            <option value="onlisans">{t('cv.option.onlisans')}</option>
            <option value="lisans">{t('cv.option.lisans')}</option>
            <option value="lisansustu">{t('cv.option.lisansustu')}</option>
          </SelectInput>
        </Field>
        <Field id="cv-mezuniyet-yili" label={t('cv.field.mezuniyet_yili')} error={t(errors.mezuniyet_yili?.message)}>
          <TextInput
            id="cv-mezuniyet-yili"
            type="number"
            min={1980}
            max={new Date().getFullYear() + 6}
            placeholder="2020"
            error={errors.mezuniyet_yili}
            {...register('mezuniyet_yili')}
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="cv-okul" label={t('cv.field.okul')}>
          <TextInput
            id="cv-okul"
            placeholder={t('cv.placeholder.okul')}
            {...register('okul')}
          />
        </Field>
        <Field id="cv-bolum" label={t('cv.field.bolum')}>
          <TextInput
            id="cv-bolum"
            placeholder={t('cv.placeholder.bolum')}
            {...register('bolum')}
          />
        </Field>
      </div>

      <Field id="cv-yabanci-dil" label={t('cv.field.yabanci_dil_egitimi')}>
        <TextInput
          id="cv-yabanci-dil"
          placeholder={t('cv.placeholder.yabanci_dil_egitimi')}
          {...register('yabanci_dil_egitimi')}
        />
      </Field>

      <div className="bg-gray-50 rounded-xl border-2 border-gray-100 p-4 space-y-3">
        <h3 className="text-xs font-black text-gray-600 uppercase tracking-wide">
          {t('cv.section.ek_egitimler')}
        </h3>
        <CheckboxField
          id="cv-devam-egitim"
          label={t('cv.field.devam_eden_egitim')}
          checked={form.watch('devam_eden_egitim')}
          onChange={(e) => form.setValue('devam_eden_egitim', e.target.checked)}
        />
        <CheckboxField
          id="cv-kabin-egitim"
          label={t('cv.field.kabin_memurlugu_egitimi')}
          checked={form.watch('kabin_memurlugu_egitimi')}
          onChange={(e) => form.setValue('kabin_memurlugu_egitimi', e.target.checked)}
        />
        <CheckboxField
          id="cv-ilk-yardim"
          label={t('cv.field.ilk_yardim_egitimi')}
          checked={form.watch('ilk_yardim_egitimi')}
          onChange={(e) => form.setValue('ilk_yardim_egitimi', e.target.checked)}
        />
        <CheckboxField
          id="cv-diksiyon"
          label={t('cv.field.diksiyon_egitimi')}
          checked={form.watch('diksiyon_egitimi')}
          onChange={(e) => form.setValue('diksiyon_egitimi', e.target.checked)}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Ek Diller Repeater
// ─────────────────────────────────────────────────────────────────────────────

// Seviye seçenekleri — PDF'e doğrudan gidecek, backend'in _DIL_SEVIYE_MAP'iyle uyumlu görsel etiketler
const DIL_SEVIYE_SECENEKLERI = [
  { value: 'A1-A2', label: 'A1-A2' },
  { value: 'B1', label: 'B1' },
  { value: 'B2', label: 'B2' },
  { value: 'C1', label: 'C1' },
  { value: 'C2 / Akıcı', label: 'C2 / Akıcı' },
];

function EkDillerRepeater({ entries, onChange, t }) {
  const [dilError, setDilError] = useState('');

  const addEntry = () => {
    onChange([...entries, { dil: '', seviye: '' }]);
    setDilError('');
  };

  const removeEntry = (idx) => {
    onChange(entries.filter((_, i) => i !== idx));
    setDilError('');
  };

  const updateEntry = (idx, field, value) => {
    const updated = entries.map((e, i) => i === idx ? { ...e, [field]: value } : e);

    // Mükerrer dil kontrolü
    if (field === 'dil' && value.trim()) {
      const key = value.trim().toLowerCase();
      const isDuplicate = updated.some((e, i) => i !== idx && e.dil.trim().toLowerCase() === key);
      setDilError(isDuplicate ? t('cv.validation.dil_mukerrer') : '');
    } else if (field === 'dil') {
      setDilError('');
    }

    onChange(updated);
  };

  return (
    <div className="space-y-3">
      {entries.map((entry, idx) => (
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.15 }}
          className="flex items-start gap-2 p-3 bg-gray-50 border-2 border-gray-200 rounded-xl"
        >
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <label
                htmlFor={`dil-adi-${idx}`}
                className="text-xs font-bold text-gray-600 uppercase tracking-wide"
              >
                {t('cv.field.dil_adi')}
              </label>
              <TextInput
                id={`dil-adi-${idx}`}
                placeholder="Almanca, Fransızca..."
                value={entry.dil}
                onChange={(e) => updateEntry(idx, 'dil', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor={`dil-sev-${idx}`}
                className="text-xs font-bold text-gray-600 uppercase tracking-wide"
              >
                {t('cv.field.dil_seviye')}
              </label>
              <SelectInput
                id={`dil-sev-${idx}`}
                value={entry.seviye}
                onChange={(e) => updateEntry(idx, 'seviye', e.target.value)}
              >
                <option value="">{t('cv.placeholder.select')}</option>
                {DIL_SEVIYE_SECENEKLERI.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </SelectInput>
            </div>
          </div>
          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => removeEntry(idx)}
            className="mt-6 p-2 rounded-lg border border-rose-200 text-rose-500 hover:bg-rose-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
            aria-label={t('cv.btn.deneyim_sil')}
          >
            <Trash2 size={14} aria-hidden="true" />
          </motion.button>
        </motion.div>
      ))}

      {dilError && (
        <p className="flex items-center gap-1 text-xs text-rose-600 font-medium" role="alert">
          <AlertCircle size={12} aria-hidden="true" />
          {dilError}
        </p>
      )}

      <motion.button
        type="button"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        onClick={addEntry}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-sky-300 text-sky-700 font-bold text-sm hover:border-sky-400 hover:bg-sky-50 transition-colors min-h-[44px] w-full justify-center"
      >
        <Plus size={16} aria-hidden="true" />
        {t('cv.action.dil_ekle')}
      </motion.button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 4 — Dil Bilgileri
// ─────────────────────────────────────────────────────────────────────────────

function Step4Form({ form, t, ekDiller, onEkDillerChange }) {
  const { register } = form;

  return (
    <div className="space-y-4">
      {/* İngilizce seviyesi — ayrı select, backend ingilizce_seviye alanına gider */}
      <Field id="cv-ing-seviye" label={t('cv.field.ingilizce_seviye')}>
        <SelectInput id="cv-ing-seviye" {...register('ingilizce_seviye')}>
          <option value="">{t('cv.placeholder.select')}</option>
          <option value="baslangic">{t('cv.option.baslangic')}</option>
          <option value="orta">{t('cv.option.orta')}</option>
          <option value="iyi">{t('cv.option.iyi')}</option>
          <option value="cok_iyi">{t('cv.option.cok_iyi')}</option>
          <option value="akici">{t('cv.option.akici')}</option>
        </SelectInput>
      </Field>

      <Field id="cv-konusma-pratigi" label={t('cv.field.konusma_pratigi')}>
        <TextAreaInput
          id="cv-konusma-pratigi"
          placeholder={t('cv.placeholder.aciklama_opsiyonel')}
          {...register('konusma_pratigi')}
        />
      </Field>

      <Field id="cv-ing-mulakat" label={t('cv.field.ingilizce_mulakat_tecrubesi')}>
        <TextAreaInput
          id="cv-ing-mulakat"
          placeholder={t('cv.placeholder.aciklama_opsiyonel')}
          {...register('ingilizce_mulakat_tecrubesi')}
        />
      </Field>

      {/* Ek Diller Repeater — yalnızca ekDiller prop geçildiğinde göster */}
      {ekDiller !== undefined && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-black text-gray-600 uppercase tracking-wide">
              {t('cv.field.diller')}
            </h3>
            {ekDiller.length === 0 && (
              <span className="text-[11px] text-gray-400 font-medium">
                {t('cv.placeholder.select') === 'Select...' ? 'Optional' : 'Opsiyonel'}
              </span>
            )}
          </div>
          <EkDillerRepeater
            entries={ekDiller}
            onChange={onEkDillerChange}
            t={t}
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CV Status Screen — replaces the old plain SuccessScreen
// Shows real-time status fetched from /cabin/cv/current
// ─────────────────────────────────────────────────────────────────────────────

function interpolateStr(str, vars) {
  if (!str) return str;
  return Object.entries(vars).reduce(
    (s, [k, v]) => s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v ?? '')),
    str
  );
}

function fmtDateCV(iso, lang) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat(lang === 'tr' ? 'tr-TR' : 'en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

const STATUS_BADGE_CLASSES = {
  pending_review: 'bg-amber-50 border-amber-300 text-amber-700',
  in_review: 'bg-sky-50 border-sky-300 text-sky-700',
  approved: 'bg-emerald-50 border-emerald-300 text-emerald-700',
  changes_requested: 'bg-rose-50 border-rose-300 text-rose-700',
};

const STATUS_ICONS = {
  pending_review: Clock,
  in_review: Eye,
  approved: CheckCircle2,
  changes_requested: MessageSquare,
};

function CVStatusScreen({ cvData, t, lang, reduced, onEditCV }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const [downloadingAts, setDownloadingAts] = useState(false);

  const status = cvData?.status;

  // Confetti for approved
  useEffect(() => {
    if (status === 'approved' && !reduced) {
      const end = Date.now() + 2500;
      const colors = ['#0EA5E9', '#10B981', '#F59E0B', '#EC4899'];
      const frame = () => {
        confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors });
        confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      requestAnimationFrame(frame);
    }
  }, [status, reduced]);

  const downloadPDF = async (variant) => {
    const isAts = variant === 'no_photo';
    if (isAts) setDownloadingAts(true);
    else setDownloading(true);
    try {
      const token = getToken();
      const url = isAts
        ? `${API_URL}/cabin/cv/pdf?variant=no_photo`
        : `${API_URL}/cabin/cv/pdf`;
      const res = await axios.get(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: 'blob',
      });
      // Güvenlik: gerçek PDF mi geliyor? Aksi hâlde blob JSON hata olabilir.
      const contentType = res.headers?.['content-type'] || '';
      if (!contentType.includes('application/pdf')) {
        let msg = t('cv.status.approved.download_error');
        try {
          const text = await res.data.text();
          const json = JSON.parse(text);
          const detail = json?.detail;
          if (typeof detail === 'object') {
            msg = (lang === 'tr' ? detail.message_tr : detail.message_en) || msg;
          } else if (typeof detail === 'string') {
            msg = detail;
          }
        } catch { /* fallback mesaj */ }
        toast.error(msg);
        return;
      }
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = isAts ? 'cv-ats.pdf' : 'cv-onaylandi.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const errStatus = err?.response?.status;
      if (errStatus === 401 || errStatus === 403) {
        toast.error(lang === 'tr' ? 'Oturum süresi doldu. Lütfen tekrar giriş yapın.' : 'Session expired. Please log in again.');
      } else {
        // responseType: 'blob' olduğunda hata gövdesi de blob gelir — text'e çevir, parse et
        let msg = t('cv.status.approved.download_error');
        try {
          const blob = err?.response?.data;
          if (blob instanceof Blob) {
            const text = await blob.text();
            const json = JSON.parse(text);
            const detail = json?.detail;
            if (typeof detail === 'object') {
              msg = (lang === 'tr' ? detail.message_tr : detail.message_en) || msg;
            } else if (typeof detail === 'string') {
              msg = detail;
            }
          }
        } catch {
          // parse başarısız — fallback mesaj kullan
        }
        toast.error(msg);
      }
    } finally {
      if (isAts) setDownloadingAts(false);
      else setDownloading(false);
    }
  };

  const handleDownloadPDF = () => downloadPDF('photo');
  const handleDownloadAts = () => downloadPDF('no_photo');

  const StatusIcon = STATUS_ICONS[status] || CheckCircle2;
  const badgeClass = STATUS_BADGE_CLASSES[status] || 'bg-gray-50 border-gray-200 text-gray-600';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35 }}
      className="flex flex-col items-center gap-6 py-12 px-6 text-center"
      data-testid="cv-status-screen"
    >
      {/* Status icon */}
      <motion.div
        initial={reduced ? {} : { scale: 0 }}
        animate={reduced ? {} : { scale: 1 }}
        transition={{ delay: 0.15, type: 'spring', stiffness: 220 }}
        className={`w-20 h-20 rounded-3xl flex items-center justify-center border-2 shadow-[0_4px_0]
          ${status === 'approved'
            ? 'bg-emerald-50 border-emerald-200 shadow-emerald-200'
            : status === 'changes_requested'
              ? 'bg-rose-50 border-rose-200 shadow-rose-200'
              : status === 'in_review'
                ? 'bg-sky-50 border-sky-200 shadow-sky-200'
                : 'bg-amber-50 border-amber-200 shadow-amber-200'
          }`}
      >
        <StatusIcon
          size={40}
          className={
            status === 'approved' ? 'text-emerald-500'
              : status === 'changes_requested' ? 'text-rose-500'
                : status === 'in_review' ? 'text-sky-500'
                  : 'text-amber-500'
          }
          aria-hidden="true"
        />
      </motion.div>

      {/* Status badge */}
      <span
        role="status"
        aria-label={t(`cv.status.${status}.badge`)}
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-bold ${badgeClass}`}
      >
        <StatusIcon size={12} aria-hidden="true" />
        {t(`cv.status.${status}.badge`)}
      </span>

      {/* Review round + submitted date */}
      <div className="flex items-center gap-3 flex-wrap justify-center text-xs text-gray-500 font-medium">
        {(cvData?.review_round || 1) > 1 && (
          <span>{interpolateStr(t('cv.status.review_round'), { round: cvData.review_round })}</span>
        )}
        {cvData?.submitted_at && (
          <span>{interpolateStr(t('cv.status.submitted_at'), { date: fmtDateCV(cvData.submitted_at, lang) })}</span>
        )}
      </div>

      {/* Title + sub */}
      <div className="max-w-sm">
        <h2 className="font-display text-xl font-black text-gray-900 mb-2">
          {t(`cv.status.${status}.title`)}
        </h2>
        <p className="text-sm text-gray-500 leading-relaxed">
          {t(`cv.status.${status}.sub`)}
        </p>
      </div>

      {/* changes_requested: instructor message */}
      {status === 'changes_requested' && cvData?.student_message && (
        <div className="w-full max-w-sm bg-rose-50 border-2 border-rose-200 rounded-xl p-4 text-left">
          <p className="text-xs font-black text-rose-600 uppercase tracking-wide mb-1.5">
            {t('cv.status.changes_requested.message_label')}
          </p>
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
            {cvData.student_message}
          </p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-col gap-3 w-full max-w-sm">
        {/* approved: two PDF download buttons */}
        {status === 'approved' && (
          <>
            <motion.button
              whileHover={{ scale: downloading ? 1 : 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleDownloadPDF}
              disabled={downloading || downloadingAts}
              className="flex items-center justify-center gap-2 py-3 px-5 bg-emerald-600 text-white font-bold rounded-xl border-2 border-emerald-700 shadow-[2px_2px_0_#047857] hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 min-h-[44px]"
            >
              {downloading ? (
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              ) : (
                <Download size={16} aria-hidden="true" />
              )}
              {downloading ? t('cv.pdf.downloading') : t('cv.pdf.download_photo')}
            </motion.button>
            <motion.button
              whileHover={{ scale: downloadingAts ? 1 : 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleDownloadAts}
              disabled={downloading || downloadingAts}
              className="flex items-center justify-center gap-2 py-3 px-5 bg-sky-600 text-white font-bold rounded-xl border-2 border-sky-700 shadow-[2px_2px_0_#0369a1] hover:bg-sky-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 min-h-[44px]"
            >
              {downloadingAts ? (
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              ) : (
                <FileText size={16} aria-hidden="true" />
              )}
              {downloadingAts ? t('cv.pdf.downloading') : t('cv.pdf.download_no_photo')}
            </motion.button>
          </>
        )}

        {/* changes_requested: edit button */}
        {status === 'changes_requested' && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={onEditCV}
            className="flex-1 flex items-center justify-center gap-2 py-3 px-5 bg-sky-600 text-white font-bold rounded-xl border-2 border-sky-700 shadow-[2px_2px_0_#0369a1] hover:bg-sky-700 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 min-h-[44px]"
          >
            {t('cv.status.changes_requested.edit_cta')}
          </motion.button>
        )}

        {/* Always show dashboard link */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate('/programs/kabin-memurlugu/dashboard')}
          className="py-3 px-5 bg-white text-gray-700 font-bold rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400 min-h-[44px]"
        >
          {t('cv.success.cta_dashboard')}
        </motion.button>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Loading Skeleton
// ─────────────────────────────────────────────────────────────────────────────

function WizardSkeleton() {
  return (
    <div className="space-y-5 animate-pulse" data-testid="cv-wizard-skeleton">
      <div className="flex gap-2 mb-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <React.Fragment key={i}>
            <Skeleton className="w-8 h-8 rounded-full" />
            {i < 2 && <Skeleton className="flex-1 h-0.5 self-center" />}
          </React.Fragment>
        ))}
      </div>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3 w-24 rounded" />
          <Skeleton className="h-11 w-full rounded-xl" />
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 6 — CV Çıktısı (Professional Output)
// Manages: unvan, hakkimda (via react-hook-form),
//          is_deneyimleri (via prop state), foto (via prop state + upload)
// ─────────────────────────────────────────────────────────────────────────────

function IsDeneyimiRepeater({ entries, onChange, t }) {
  const addEntry = () => {
    onChange([
      ...entries,
      { pozisyon: '', sirket: '', baslangic: '', bitis: '', devam_ediyor: false, maddeler: [''] },
    ]);
  };

  const removeEntry = (idx) => {
    onChange(entries.filter((_, i) => i !== idx));
  };

  const updateEntry = (idx, field, value) => {
    const updated = entries.map((e, i) => i === idx ? { ...e, [field]: value } : e);
    onChange(updated);
  };

  const addMadde = (idx) => {
    if ((entries[idx].maddeler || []).length >= 4) return;
    const updated = entries.map((e, i) =>
      i === idx ? { ...e, maddeler: [...(e.maddeler || []), ''] } : e
    );
    onChange(updated);
  };

  const updateMadde = (entryIdx, maddeIdx, value) => {
    const updated = entries.map((e, i) => {
      if (i !== entryIdx) return e;
      const maddeler = [...(e.maddeler || [])];
      maddeler[maddeIdx] = value;
      return { ...e, maddeler };
    });
    onChange(updated);
  };

  const removeMadde = (entryIdx, maddeIdx) => {
    const updated = entries.map((e, i) => {
      if (i !== entryIdx) return e;
      return { ...e, maddeler: (e.maddeler || []).filter((_, mi) => mi !== maddeIdx) };
    });
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      {entries.map((entry, idx) => (
        <div
          key={idx}
          className="border-2 border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-black text-gray-600 uppercase tracking-wide">
              {t('cv.section.is_deneyimleri')} #{idx + 1}
            </span>
            <motion.button
              type="button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => removeEntry(idx)}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold text-rose-600 border border-rose-200 hover:bg-rose-50 transition-colors min-h-[32px]"
              aria-label={t('cv.btn.deneyim_sil')}
            >
              <Trash2 size={13} aria-hidden="true" />
              {t('cv.btn.deneyim_sil')}
            </motion.button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field id={`den-poz-${idx}`} label={t('cv.field.is_pozisyon')}>
              <TextInput
                id={`den-poz-${idx}`}
                placeholder={t('cv.placeholder.is_pozisyon')}
                value={entry.pozisyon || ''}
                onChange={(e) => updateEntry(idx, 'pozisyon', e.target.value)}
              />
            </Field>
            <Field id={`den-sir-${idx}`} label={t('cv.field.is_sirket')}>
              <TextInput
                id={`den-sir-${idx}`}
                placeholder={t('cv.placeholder.is_sirket')}
                value={entry.sirket || ''}
                onChange={(e) => updateEntry(idx, 'sirket', e.target.value)}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field id={`den-bas-${idx}`} label={t('cv.field.is_baslangic')}>
              <TextInput
                id={`den-bas-${idx}`}
                placeholder={t('cv.placeholder.is_baslangic')}
                value={entry.baslangic || ''}
                onChange={(e) => updateEntry(idx, 'baslangic', e.target.value)}
              />
            </Field>
            {!entry.devam_ediyor && (
              <Field id={`den-bit-${idx}`} label={t('cv.field.is_bitis')}>
                <TextInput
                  id={`den-bit-${idx}`}
                  placeholder={t('cv.placeholder.is_bitis')}
                  value={entry.bitis || ''}
                  onChange={(e) => updateEntry(idx, 'bitis', e.target.value)}
                />
              </Field>
            )}
          </div>

          <CheckboxField
            id={`den-devam-${idx}`}
            label={t('cv.field.is_devam_ediyor')}
            checked={!!entry.devam_ediyor}
            onChange={(e) => updateEntry(idx, 'devam_ediyor', e.target.checked)}
          />

          {/* Maddeler */}
          <div className="space-y-2">
            <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">
              {t('cv.field.is_maddeler')}
            </p>
            {(entry.maddeler || []).map((madde, mi) => (
              <div key={mi} className="flex items-center gap-2">
                <TextInput
                  id={`den-madde-${idx}-${mi}`}
                  placeholder={t('cv.placeholder.is_madde')}
                  value={madde}
                  onChange={(e) => updateMadde(idx, mi, e.target.value)}
                  className="flex-1"
                />
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => removeMadde(idx, mi)}
                  className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:text-rose-600 hover:border-rose-200 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label={t('cv.btn.madde_sil')}
                >
                  <X size={14} aria-hidden="true" />
                </motion.button>
              </div>
            ))}
            {(entry.maddeler || []).length < 4 && (
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => addMadde(idx)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-sky-700 border border-sky-200 rounded-lg hover:bg-sky-50 transition-colors min-h-[36px]"
              >
                <Plus size={12} aria-hidden="true" />
                {t('cv.btn.madde_ekle')}
              </motion.button>
            )}
          </div>
        </div>
      ))}

      <motion.button
        type="button"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        onClick={addEntry}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-sky-300 text-sky-700 font-bold text-sm hover:border-sky-400 hover:bg-sky-50 transition-colors min-h-[44px] w-full justify-center"
      >
        <Plus size={16} aria-hidden="true" />
        {t('cv.btn.deneyim_ekle')}
      </motion.button>
    </div>
  );
}

function FotoUploadSection({ fotoUrl, t, onUpload, onDelete, uploading, deleting }) {
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Client-side validation
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      alert(t('cv.validation.foto_format'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert(t('cv.validation.foto_size'));
      return;
    }
    await onUpload(file);
    // Reset input so same file can be re-selected if needed
    e.target.value = '';
  };

  return (
    <div className="space-y-3">
      {fotoUrl ? (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-24 h-24 rounded-2xl border-2 border-gray-200 overflow-hidden bg-gray-100 flex-shrink-0">
            <img
              src={fotoUrl}
              alt={t('cv.foto.preview_alt')}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || deleting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-sky-700 border-2 border-sky-200 rounded-xl hover:bg-sky-50 transition-colors disabled:opacity-50 min-h-[40px]"
            >
              <Camera size={14} aria-hidden="true" />
              {uploading ? t('cv.btn.foto_uploading') : t('cv.btn.foto_yukle')}
            </motion.button>
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={onDelete}
              disabled={uploading || deleting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-rose-600 border-2 border-rose-200 rounded-xl hover:bg-rose-50 transition-colors disabled:opacity-50 min-h-[40px]"
            >
              {deleting ? (
                <Loader2 size={14} className="animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 size={14} aria-hidden="true" />
              )}
              {deleting ? t('cv.btn.foto_deleting') : t('cv.btn.foto_kaldir')}
            </motion.button>
          </div>
        </div>
      ) : (
        <motion.button
          type="button"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex flex-col items-center justify-center gap-2 w-full py-8 border-2 border-dashed border-gray-300 rounded-2xl text-gray-500 hover:border-sky-400 hover:text-sky-600 hover:bg-sky-50 transition-colors disabled:opacity-50 min-h-[120px]"
        >
          {uploading ? (
            <Loader2 size={28} className="animate-spin text-sky-500" aria-hidden="true" />
          ) : (
            <Camera size={28} aria-hidden="true" />
          )}
          <span className="text-sm font-bold">
            {uploading ? t('cv.btn.foto_uploading') : t('cv.btn.foto_yukle')}
          </span>
          <span className="text-xs text-gray-500">JPEG / PNG, max 2 MB</span>
        </motion.button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        className="sr-only"
        onChange={handleFileChange}
        aria-label={t('cv.btn.foto_yukle')}
      />
    </div>
  );
}

function Step6Form({ form, t, isDeneyimleri, onIsDeneyimleriChange, fotoUrl, onFotoUpload, onFotoDelete, fotoUploading, fotoDeleting }) {
  const { register, formState: { errors }, watch } = form;
  const hakkimda = watch('hakkimda') || '';
  const charCount = hakkimda.length;

  return (
    <div className="space-y-6">
      {/* Unvan */}
      <Field id="cv-unvan" label={t('cv.field.unvan')}>
        <TextInput
          id="cv-unvan"
          placeholder={t('cv.placeholder.unvan')}
          {...register('unvan')}
        />
      </Field>

      {/* Hakkımda */}
      <Field id="cv-hakkimda" label={t('cv.field.hakkimda')} error={t(errors.hakkimda?.message)}>
        <TextAreaInput
          id="cv-hakkimda"
          placeholder={t('cv.placeholder.hakkimda')}
          rows={5}
          maxLength={600}
          error={errors.hakkimda}
          {...register('hakkimda')}
        />
        <p className={`text-[11px] mt-0.5 text-right font-medium ${charCount > 580 ? 'text-amber-600' : 'text-gray-500'}`}>
          {t('cv.hint.hakkimda_counter').replace('{count}', charCount)}
        </p>
      </Field>

      {/* İş Deneyimleri Repeater */}
      <div>
        <h3 className="text-xs font-black text-gray-600 uppercase tracking-wide mb-3">
          {t('cv.section.is_deneyimleri')}
        </h3>
        <IsDeneyimiRepeater
          entries={isDeneyimleri}
          onChange={onIsDeneyimleriChange}
          t={t}
        />
      </div>

      {/* Profil Fotoğrafı */}
      <div>
        <h3 className="text-xs font-black text-gray-600 uppercase tracking-wide mb-3">
          {t('cv.section.foto')}
        </h3>
        <FotoUploadSection
          fotoUrl={fotoUrl}
          t={t}
          onUpload={onFotoUpload}
          onDelete={onFotoDelete}
          uploading={fotoUploading}
          deleting={fotoDeleting}
        />
      </div>

      {/* Referanslar notu */}
      <div className="flex items-start gap-2 p-3 bg-amber-50 border-2 border-amber-200 rounded-xl">
        <Info size={15} className="text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-0.5">
            {t('cv.section.referans_not')}
          </p>
          <p className="text-xs text-amber-700 leading-relaxed">
            {t('cv.hint.referans')}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Europass Step 0 — Welcome (Faz 2 iskelet)
// Profil özet kartı + boş uyarısı + KVKK inline card + "Başlayalım" CTA
// ─────────────────────────────────────────────────────────────────────────────

function WelcomeStep({ cvData, kvkkConsented, onKvkkChange, t, onGoToEdit }) {
  const [kvkkExpanded, setKvkkExpanded] = useState(false);
  const [kvkkChecked, setKvkkChecked] = useState(kvkkConsented);

  // Hardening B6: prop değişince local state'i senkronize et (stale prop guard)
  useEffect(() => {
    setKvkkChecked(kvkkConsented);
  }, [kvkkConsented]);

  const filledCount = countFilledSections(cvData);
  const isEmpty = filledCount === 0;

  const handleCheck = (e) => {
    setKvkkChecked(e.target.checked);
    onKvkkChange(e.target.checked);
  };

  return (
    <div className="space-y-5">
      {/* Karşılama başlığı */}
      <div className="text-center py-3">
        <h3 className="font-display text-xl font-black text-gray-900 mb-1">
          {t('cv_wizard.welcome.title')}
        </h3>
        <p className="text-sm text-gray-500 leading-relaxed">
          {t('cv_wizard.welcome.subtitle')}
        </p>
      </div>

      {/* Profil özet kartı */}
      <div className="bg-sky-50 border-2 border-sky-200 rounded-xl p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-sky-100 border-2 border-sky-200 flex items-center justify-center flex-shrink-0">
          <User size={20} className="text-sky-600" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black text-sky-900">
            {interpolateStr(t('cv_wizard.welcome.profile_summary'), { n: filledCount })}
          </p>
          <p className="text-xs text-sky-700 mt-0.5 truncate">
            {cvData?.kisisel?.ad_soyad || t('cv_wizard.welcome.no_name')}
          </p>
        </div>
      </div>

      {/* Boş profil uyarısı */}
      {isEmpty && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 flex items-start gap-3" role="alert">
          <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-800 leading-snug">
              {t('cv_wizard.welcome.empty_warning')}
            </p>
            <button
              type="button"
              onClick={() => onGoToEdit?.()}
              className="text-xs font-bold text-amber-700 underline hover:text-amber-900 mt-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600"
            >
              {t('cv_wizard.welcome.go_to_profile')}
            </button>
          </div>
        </div>
      )}

      {/* KVKK inline card */}
      <div className="border-2 border-gray-200 rounded-xl overflow-hidden" role="region" aria-label={t('cv_wizard.welcome.kvkk_title')}>
        <button
          type="button"
          onClick={() => setKvkkExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
          aria-expanded={kvkkExpanded}
        >
          <span className="flex items-center gap-2 text-sm font-bold text-gray-700">
            <ClipboardList size={16} className="text-sky-600" aria-hidden="true" />
            {t('cv_wizard.welcome.kvkk_title')}
          </span>
          <ChevronDown
            size={16}
            className={`text-gray-500 transition-transform duration-200 ${kvkkExpanded ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
        {kvkkExpanded && (
          <div className="px-4 py-3 text-xs text-gray-600 leading-relaxed max-h-40 overflow-y-auto border-t border-gray-100">
            {t('cv.kvkk.full_text')}
          </div>
        )}
        <div className="px-4 py-3 border-t border-gray-100">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              id="welcome-kvkk-consent"
              checked={kvkkChecked}
              onChange={handleCheck}
              className="w-5 h-5 mt-0.5 rounded border-2 border-gray-300 accent-sky-600 flex-shrink-0 focus-visible:outline-2 focus-visible:outline-sky-600"
            />
            <span className="text-sm font-medium text-gray-700 leading-snug">
              {t('cv.kvkk.consent_label')}
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Faz 4 — Edit Adımı
// InlineField, canvas templates → CVCanvasPreview.jsx'e taşındı (ADIM 2)
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Faz 7 — MotivasyonForm (isolated controlled component)
// Group F of ApplicationInfoDialog; can be relocated to its own screen later.
// ─────────────────────────────────────────────────────────────────────────────

function MotivasyonForm({ formData, onChange, t }) {
  const set = (field, val) => onChange({ ...formData, [field]: val });
  return (
    <div className="space-y-4">
      <Field id="ai-neden" label={t('cv.field.neden_kabin_memuru')}>
        <TextAreaInput
          id="ai-neden"
          rows={3}
          placeholder={t('cv.placeholder.neden_kabin_memuru')}
          value={formData.neden_kabin_memuru || ''}
          onChange={(e) => set('neden_kabin_memuru', e.target.value)}
        />
      </Field>
      <Field id="ai-nezaman" label={t('cv.field.ne_zamandir_ister')}>
        <TextInput
          id="ai-nezaman"
          placeholder={t('cv.placeholder.ne_zamandir_ister')}
          value={formData.ne_zamandir_ister || ''}
          onChange={(e) => set('ne_zamandir_ister', e.target.value)}
        />
      </Field>
      <Field id="ai-zorluk" label={t('cv.field.zorluk_farkindaligi')}>
        <TextAreaInput
          id="ai-zorluk"
          rows={2}
          placeholder={t('cv.placeholder.aciklama_opsiyonel')}
          value={formData.zorluk_farkindaligi || ''}
          onChange={(e) => set('zorluk_farkindaligi', e.target.value)}
        />
      </Field>
      <Field id="ai-havayolu" label={t('cv.field.havayolu_beklentisi')}>
        <TextAreaInput
          id="ai-havayolu"
          rows={2}
          placeholder={t('cv.placeholder.aciklama_opsiyonel')}
          value={formData.havayolu_beklentisi || ''}
          onChange={(e) => set('havayolu_beklentisi', e.target.value)}
        />
      </Field>
      <Field id="ai-besyil" label={t('cv.field.bes_yil_sonra')}>
        <TextAreaInput
          id="ai-besyil"
          rows={2}
          placeholder={t('cv.placeholder.bes_yil_sonra')}
          value={formData.bes_yil_sonra || ''}
          onChange={(e) => set('bes_yil_sonra', e.target.value)}
        />
      </Field>
      <div className="bg-gray-50 rounded-xl border-2 border-gray-100 p-4 space-y-3">
        <h4 className="text-xs font-black text-gray-600 uppercase tracking-wide">
          {t('cv.section.uygunluk')}
        </h4>
        <CheckboxField
          id="ai-uzun-ucus"
          label={t('cv.field.uzun_ucus_uygunlugu')}
          checked={!!formData.uzun_ucus_uygunlugu}
          onChange={(e) => set('uzun_ucus_uygunlugu', e.target.checked)}
        />
        <CheckboxField
          id="ai-vardiya-uyg"
          label={t('cv.field.vardiya_uygunlugu')}
          checked={!!formData.vardiya_uygunlugu}
          onChange={(e) => set('vardiya_uygunlugu', e.target.checked)}
        />
        <CheckboxField
          id="ai-sehir-deg"
          label={t('cv.field.sehir_degisimi_uygunlugu')}
          checked={!!formData.sehir_degisimi_uygunlugu}
          onChange={(e) => set('sehir_degisimi_uygunlugu', e.target.checked)}
        />
        <CheckboxField
          id="ai-uniforma"
          label={t('cv.field.uniforma_disiplini')}
          checked={!!formData.uniforma_disiplini}
          onChange={(e) => set('uniforma_disiplini', e.target.checked)}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Faz 7 — ApplicationInfoDialog (Hidden-Meta alanları)
// ─────────────────────────────────────────────────────────────────────────────

function AccordionGroup({ id, label, open, onToggle, children }) {
  return (
    <fieldset className="border-2 border-gray-100 rounded-xl overflow-hidden">
      <legend className="sr-only">{label}</legend>
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
        aria-expanded={open}
      >
        <span className="text-xs font-black text-gray-700 uppercase tracking-wide">{label}</span>
        <ChevronDown
          size={14}
          className={`text-gray-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 py-4 space-y-4 border-t border-gray-100">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </fieldset>
  );
}

function ApplicationInfoDialog({ open, onClose, cvData, localEdits, onSave, t }) {
  // Seed from localEdits.application_info merged over cvData
  const seedData = () => {
    const ai = localEdits?.application_info || {};
    const kisisel = cvData?.kisisel || {};
    const egitim = cvData?.egitim || {};
    const dil = cvData?.dil || {};
    const isDen = cvData?.is_deneyimi || {};
    const mot = cvData?.motivasyon || {};
    return {
      // Grup A
      cinsiyet:               ai.cinsiyet              ?? kisisel.cinsiyet              ?? '',
      boy_cm:                 ai.boy_cm                ?? kisisel.boy_cm                ?? '',
      kilo_kg:                ai.kilo_kg               ?? kisisel.kilo_kg               ?? '',
      pasaport_var:           ai.pasaport_var           ?? kisisel.pasaport_var           ?? false,
      seyahat_engeli:         ai.seyahat_engeli         ?? kisisel.seyahat_engeli         ?? false,
      seyahat_engeli_aciklama: ai.seyahat_engeli_aciklama ?? kisisel.seyahat_engeli_aciklama ?? '',
      adli_sicil:             ai.adli_sicil             ?? kisisel.adli_sicil             ?? false,
      adli_sicil_aciklama:    ai.adli_sicil_aciklama    ?? kisisel.adli_sicil_aciklama    ?? '',
      // Grup B
      onceki_havayolu_basvurusu: ai.onceki_havayolu_basvurusu ?? kisisel.onceki_havayolu_basvurusu ?? false,
      mulakate_girdi_mi:      ai.mulakate_girdi_mi      ?? kisisel.mulakate_girdi_mi      ?? false,
      elendigi_asama:         ai.elendigi_asama         ?? kisisel.elendigi_asama         ?? '',
      // Grup C
      konusma_pratigi:        ai.konusma_pratigi        ?? dil.konusma_pratigi            ?? '',
      ingilizce_mulakat_tecrubesi: ai.ingilizce_mulakat_tecrubesi ?? dil.ingilizce_mulakat_tecrubesi ?? '',
      // Grup D
      kabin_memurlugu_egitimi: ai.kabin_memurlugu_egitimi ?? egitim['kabin_memurluğu_egitimi'] ?? false,
      ilk_yardim_egitimi:     ai.ilk_yardim_egitimi     ?? egitim.ilk_yardim_egitimi     ?? false,
      diksiyon_egitimi:       ai.diksiyon_egitimi       ?? egitim.diksiyon_egitimi       ?? false,
      // Grup E
      calistimi:              ai.calistimi              ?? isDen.calistimi               ?? false,
      son_is_yeri:            ai.son_is_yeri            ?? isDen.son_is_yeri             ?? '',
      pozisyon:               ai.pozisyon               ?? isDen.pozisyon                ?? '',
      sure:                   ai.sure                   ?? isDen.sure                    ?? '',
      musteri_iliskileri_deneyimi: ai.musteri_iliskileri_deneyimi ?? isDen.musteri_iliskileri_deneyimi ?? '',
      satis_deneyimi:         ai.satis_deneyimi         ?? isDen.satis_deneyimi          ?? '',
      yogun_tempo_deneyimi:   ai.yogun_tempo_deneyimi   ?? isDen.yogun_tempo_deneyimi    ?? '',
      vardiya_deneyimi:       ai.vardiya_deneyimi       ?? isDen.vardiya_deneyimi        ?? '',
      ekip_deneyimi:          ai.ekip_deneyimi          ?? isDen.ekip_deneyimi           ?? '',
      zor_musteri_deneyimi:   ai.zor_musteri_deneyimi   ?? isDen.zor_musteri_deneyimi    ?? '',
      // Grup F (MotivasyonForm)
      neden_kabin_memuru:     ai.neden_kabin_memuru     ?? mot.neden_kabin_memuru        ?? '',
      ne_zamandir_ister:      ai.ne_zamandir_ister      ?? mot.ne_zamandir_ister         ?? '',
      zorluk_farkindaligi:    ai.zorluk_farkindaligi    ?? mot['zorluk_farkindaliği']    ?? '',
      uzun_ucus_uygunlugu:    ai.uzun_ucus_uygunlugu    ?? mot.uzun_ucus_uygunlugu       ?? false,
      vardiya_uygunlugu:      ai.vardiya_uygunlugu      ?? mot.vardiya_uygunlugu         ?? false,
      sehir_degisimi_uygunlugu: ai.sehir_degisimi_uygunlugu ?? mot.sehir_degisimi_uygunlugu ?? false,
      uniforma_disiplini:     ai.uniforma_disiplini     ?? mot.uniforma_disiplini        ?? false,
      havayolu_beklentisi:    ai.havayolu_beklentisi    ?? mot.havayolu_beklentisi       ?? '',
      bes_yil_sonra:          ai.bes_yil_sonra          ?? mot.bes_yil_sonra             ?? '',
    };
  };

  const [localData, setLocalData] = useState(seedData);
  const [openGroup, setOpenGroup] = useState('A');

  // Re-seed when dialog re-opens
  useEffect(() => {
    if (open) setLocalData(seedData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (field, val) => setLocalData((prev) => ({ ...prev, [field]: val }));

  const handleToggleGroup = (id) => setOpenGroup((prev) => (prev === id ? null : id));

  const handleSave = () => {
    onSave('application_info', localData);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg rounded-2xl border-2 border-gray-200 shadow-[4px_4px_0_#E5E7EB] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-black text-gray-900">
            {t('cv_wizard.application_info.dialog_title')}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-3 space-y-2">

          {/* Grup A — Fiziksel Nitelikler */}
          <AccordionGroup
            id="A"
            label={t('cv_wizard.application_info.group_a')}
            open={openGroup === 'A'}
            onToggle={handleToggleGroup}
          >
            <Field id="ai-cinsiyet" label={t('cv.field.cinsiyet')}>
              <SelectInput
                id="ai-cinsiyet"
                value={localData.cinsiyet || ''}
                onChange={(e) => set('cinsiyet', e.target.value)}
              >
                <option value="">{t('cv.placeholder.select')}</option>
                <option value="kadin">{t('cv.option.kadin')}</option>
                <option value="erkek">{t('cv.option.erkek')}</option>
                <option value="diger">{t('cv.option.diger')}</option>
              </SelectInput>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field id="ai-boy" label={t('cv.field.boy_cm')}>
                <TextInput
                  id="ai-boy"
                  type="number"
                  min={140}
                  max={210}
                  placeholder="170"
                  value={localData.boy_cm ?? ''}
                  onChange={(e) => set('boy_cm', e.target.value)}
                />
              </Field>
              <Field id="ai-kilo" label={t('cv.field.kilo_kg')}>
                <TextInput
                  id="ai-kilo"
                  type="number"
                  min={40}
                  max={150}
                  placeholder="65"
                  value={localData.kilo_kg ?? ''}
                  onChange={(e) => set('kilo_kg', e.target.value)}
                />
              </Field>
            </div>
            <CheckboxField
              id="ai-pasaport"
              label={t('cv.field.pasaport_var')}
              checked={!!localData.pasaport_var}
              onChange={(e) => set('pasaport_var', e.target.checked)}
            />
            <CheckboxField
              id="ai-seyahat"
              label={t('cv.field.seyahat_engeli')}
              checked={!!localData.seyahat_engeli}
              onChange={(e) => set('seyahat_engeli', e.target.checked)}
            />
            {localData.seyahat_engeli && (
              <Field id="ai-seyahat-acik" label={t('cv.field.seyahat_engeli_aciklama')}>
                <TextInput
                  id="ai-seyahat-acik"
                  placeholder={t('cv.placeholder.aciklama')}
                  value={localData.seyahat_engeli_aciklama || ''}
                  onChange={(e) => set('seyahat_engeli_aciklama', e.target.value)}
                />
              </Field>
            )}
            <CheckboxField
              id="ai-adli"
              label={t('cv.field.adli_sicil')}
              checked={!!localData.adli_sicil}
              onChange={(e) => set('adli_sicil', e.target.checked)}
            />
            {localData.adli_sicil && (
              <Field id="ai-adli-acik" label={t('cv.field.adli_sicil_aciklama')}>
                <TextInput
                  id="ai-adli-acik"
                  placeholder={t('cv.placeholder.aciklama')}
                  value={localData.adli_sicil_aciklama || ''}
                  onChange={(e) => set('adli_sicil_aciklama', e.target.value)}
                />
              </Field>
            )}
          </AccordionGroup>

          {/* Grup B — Başvuru Geçmişi */}
          <AccordionGroup
            id="B"
            label={t('cv_wizard.application_info.group_b')}
            open={openGroup === 'B'}
            onToggle={handleToggleGroup}
          >
            <CheckboxField
              id="ai-onceki"
              label={t('cv.field.onceki_havayolu_basvurusu')}
              checked={!!localData.onceki_havayolu_basvurusu}
              onChange={(e) => set('onceki_havayolu_basvurusu', e.target.checked)}
            />
            {localData.onceki_havayolu_basvurusu && (
              <>
                <CheckboxField
                  id="ai-mulakat"
                  label={t('cv.field.mulakate_girdi_mi')}
                  checked={!!localData.mulakate_girdi_mi}
                  onChange={(e) => set('mulakate_girdi_mi', e.target.checked)}
                />
                {localData.mulakate_girdi_mi && (
                  <Field id="ai-elendi" label={t('cv.field.elendigi_asama')}>
                    <TextInput
                      id="ai-elendi"
                      placeholder={t('cv.placeholder.elendigi_asama')}
                      value={localData.elendigi_asama || ''}
                      onChange={(e) => set('elendigi_asama', e.target.value)}
                    />
                  </Field>
                )}
              </>
            )}
          </AccordionGroup>

          {/* Grup C — Dil Deneyimi */}
          <AccordionGroup
            id="C"
            label={t('cv_wizard.application_info.group_c')}
            open={openGroup === 'C'}
            onToggle={handleToggleGroup}
          >
            <Field id="ai-konusma" label={t('cv.field.konusma_pratigi')}>
              <TextAreaInput
                id="ai-konusma"
                rows={2}
                placeholder={t('cv.placeholder.aciklama_opsiyonel')}
                value={localData.konusma_pratigi || ''}
                onChange={(e) => set('konusma_pratigi', e.target.value)}
              />
            </Field>
            <Field id="ai-ing-mulakat" label={t('cv.field.ingilizce_mulakat_tecrubesi')}>
              <TextAreaInput
                id="ai-ing-mulakat"
                rows={2}
                placeholder={t('cv.placeholder.aciklama_opsiyonel')}
                value={localData.ingilizce_mulakat_tecrubesi || ''}
                onChange={(e) => set('ingilizce_mulakat_tecrubesi', e.target.value)}
              />
            </Field>
          </AccordionGroup>

          {/* Grup D — Ek Eğitimler */}
          <AccordionGroup
            id="D"
            label={t('cv_wizard.application_info.group_d')}
            open={openGroup === 'D'}
            onToggle={handleToggleGroup}
          >
            <CheckboxField
              id="ai-kabin"
              label={t('cv.field.kabin_memurlugu_egitimi')}
              checked={!!localData.kabin_memurlugu_egitimi}
              onChange={(e) => set('kabin_memurlugu_egitimi', e.target.checked)}
            />
            <CheckboxField
              id="ai-ilkyardim"
              label={t('cv.field.ilk_yardim_egitimi')}
              checked={!!localData.ilk_yardim_egitimi}
              onChange={(e) => set('ilk_yardim_egitimi', e.target.checked)}
            />
            <CheckboxField
              id="ai-diksiyon"
              label={t('cv.field.diksiyon_egitimi')}
              checked={!!localData.diksiyon_egitimi}
              onChange={(e) => set('diksiyon_egitimi', e.target.checked)}
            />
          </AccordionGroup>

          {/* Grup E — İş Deneyimi Niteleme */}
          <AccordionGroup
            id="E"
            label={t('cv_wizard.application_info.group_e')}
            open={openGroup === 'E'}
            onToggle={handleToggleGroup}
          >
            <CheckboxField
              id="ai-calistimi"
              label={t('cv.field.calistimi')}
              checked={!!localData.calistimi}
              onChange={(e) => set('calistimi', e.target.checked)}
            />
            {localData.calistimi && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field id="ai-son-is" label={t('cv.field.son_is_yeri')}>
                    <TextInput
                      id="ai-son-is"
                      placeholder={t('cv.placeholder.son_is_yeri')}
                      value={localData.son_is_yeri || ''}
                      onChange={(e) => set('son_is_yeri', e.target.value)}
                    />
                  </Field>
                  <Field id="ai-pozisyon" label={t('cv.field.pozisyon')}>
                    <TextInput
                      id="ai-pozisyon"
                      placeholder={t('cv.placeholder.pozisyon')}
                      value={localData.pozisyon || ''}
                      onChange={(e) => set('pozisyon', e.target.value)}
                    />
                  </Field>
                </div>
                <Field id="ai-sure" label={t('cv.field.sure')}>
                  <TextInput
                    id="ai-sure"
                    placeholder={t('cv.placeholder.sure')}
                    value={localData.sure || ''}
                    onChange={(e) => set('sure', e.target.value)}
                  />
                </Field>
              </>
            )}
            <Field id="ai-musteri" label={t('cv.field.musteri_iliskileri_deneyimi')}>
              <TextAreaInput id="ai-musteri" rows={2} placeholder={t('cv.placeholder.aciklama_opsiyonel')} value={localData.musteri_iliskileri_deneyimi || ''} onChange={(e) => set('musteri_iliskileri_deneyimi', e.target.value)} />
            </Field>
            <Field id="ai-satis" label={t('cv.field.satis_deneyimi')}>
              <TextAreaInput id="ai-satis" rows={2} placeholder={t('cv.placeholder.aciklama_opsiyonel')} value={localData.satis_deneyimi || ''} onChange={(e) => set('satis_deneyimi', e.target.value)} />
            </Field>
            <Field id="ai-yogun" label={t('cv.field.yogun_tempo_deneyimi')}>
              <TextAreaInput id="ai-yogun" rows={2} placeholder={t('cv.placeholder.aciklama_opsiyonel')} value={localData.yogun_tempo_deneyimi || ''} onChange={(e) => set('yogun_tempo_deneyimi', e.target.value)} />
            </Field>
            <Field id="ai-vardiya" label={t('cv.field.vardiya_deneyimi')}>
              <TextAreaInput id="ai-vardiya" rows={2} placeholder={t('cv.placeholder.aciklama_opsiyonel')} value={localData.vardiya_deneyimi || ''} onChange={(e) => set('vardiya_deneyimi', e.target.value)} />
            </Field>
            <Field id="ai-ekip" label={t('cv.field.ekip_deneyimi')}>
              <TextAreaInput id="ai-ekip" rows={2} placeholder={t('cv.placeholder.aciklama_opsiyonel')} value={localData.ekip_deneyimi || ''} onChange={(e) => set('ekip_deneyimi', e.target.value)} />
            </Field>
            <Field id="ai-zor" label={t('cv.field.zor_musteri_deneyimi')}>
              <TextAreaInput id="ai-zor" rows={2} placeholder={t('cv.placeholder.aciklama_opsiyonel')} value={localData.zor_musteri_deneyimi || ''} onChange={(e) => set('zor_musteri_deneyimi', e.target.value)} />
            </Field>
          </AccordionGroup>

          {/* Grup F — Motivasyon (MotivasyonForm) */}
          <AccordionGroup
            id="F"
            label={t('cv_wizard.application_info.group_f')}
            open={openGroup === 'F'}
            onToggle={handleToggleGroup}
          >
            {/* Amber bilgi notu */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 border-2 border-amber-200 rounded-xl">
              <Info size={14} className="text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-xs text-amber-700 leading-relaxed">
                {t('cv_wizard.application_info.motivation_note')}
              </p>
            </div>
            <MotivasyonForm formData={localData} onChange={setLocalData} t={t} />
          </AccordionGroup>
        </div>

        {/* Footer */}
        <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100 min-h-[44px]">
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleSave}
            className="flex-1 py-2.5 px-4 bg-sky-600 text-white font-bold rounded-xl border-2 border-sky-700 shadow-[2px_2px_0_#0369a1] hover:bg-sky-700 transition-colors min-h-[44px]"
          >
            {t('cv_wizard.application_info.save')}
          </motion.button>
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={onClose}
            className="py-2.5 px-4 bg-white text-gray-700 font-bold rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-colors min-h-[44px]"
          >
            {t('cv_wizard.application_info.cancel')}
          </motion.button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// createRenderF → CVCanvasPreview.jsx'e taşındı

// EditableExperienceList, EditableDilList, EditableSertifikalarList → CVCanvasPreview.jsx'e taşındı

// ClassicSectionHeading, CanvasFotoCircle, ClassicTemplate,
// ModernSectionHeading, ModernTemplate, MinimalTemplate, CVCanvas
// → CVCanvasPreview.jsx'e taşındı (ADIM 2)
// TEMPLATE_IDS → CVCanvasPreview.jsx'ten import edilmektedir


/**
 * TemplateToolbar — canvas üstünde şablon seçici + autosave indicator.
 * role="tablist"; roving tabindex; containerRef ile klavye yönlendirmesi.
 */
function TemplateToolbar({ templateId, onTemplateChange, autosaveStatus, t, onAppInfoClick, appInfoOpen }) {
  const tabListRef = useRef(null);

  const handleKeyDown = (e, idx) => {
    let newIdx = idx;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      newIdx = (idx + 1) % TEMPLATE_IDS.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      newIdx = (idx - 1 + TEMPLATE_IDS.length) % TEMPLATE_IDS.length;
    } else {
      return;
    }
    onTemplateChange(TEMPLATE_IDS[newIdx]);
    const tabs = tabListRef.current?.querySelectorAll('[role="tab"]');
    tabs?.[newIdx]?.focus();
  };

  return (
    <div className="flex items-center justify-between gap-3 pb-3 mb-1 border-b-2 border-gray-100">
      <div
        ref={tabListRef}
        role="tablist"
        aria-label={t('cv_wizard.canvas.toolbar_label')}
        className="flex bg-gray-100 rounded-xl p-0.5 gap-0.5"
      >
        {TEMPLATE_IDS.map((id, idx) => {
          const isSelected = id === templateId;
          return (
            <button
              key={id}
              role="tab"
              aria-selected={isSelected}
              tabIndex={isSelected ? 0 : -1}
              onClick={() => onTemplateChange(id)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all min-h-[32px] focus-visible:outline-2 focus-visible:outline-sky-500 focus-visible:outline-offset-1 ${
                isSelected
                  ? 'bg-white shadow-sm text-sky-700 border border-sky-200'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t(`cv_wizard.canvas.template_${id}`)}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        {/* Faz 7 — Başvuru Bilgileri dialog açma butonu */}
        {onAppInfoClick && (
          <motion.button
            type="button"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            onClick={onAppInfoClick}
            aria-haspopup="dialog"
            aria-expanded={!!appInfoOpen}
            aria-label={t('cv_wizard.application_info.btn_label')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 font-bold rounded-lg border-2 border-amber-200 text-xs hover:bg-amber-100 transition-colors min-h-[32px] focus-visible:outline-2 focus-visible:outline-amber-500"
          >
            <ClipboardList size={14} aria-hidden="true" />
            <span className="hidden sm:inline">{t('cv_wizard.application_info.btn_label')}</span>
          </motion.button>
        )}
        <AutosaveIndicator status={autosaveStatus} t={t} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Faz 6 — MobileEditSheet (Bulgu 3 fix: merged seeding from canvas state)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Mobile bottom-sheet edit for a section.
 * Bulgu 3: seeds from cvData + localEdits.canvas_kisisel/canvas_egitim + canvasDilDiller.
 */
function MobileEditSheet({
  section, cvData, localEdits, canvasDilDiller,
  t, onClose, onSave, isDeneyimleri, onIsDeneyimleriChange,
  canvasSertifikalar, onSertifikaAdd: _onSertifikaAdd, onSertifikaDelete: _onSertifikaDelete,
}) {
  // Bulgu 3: kisisel — merge canvas_kisisel overrides over cvData.kisisel
  const mobileForm1 = useForm({ resolver: zodResolver(step1Schema), defaultValues: (() => {
    const baseKis = { ...(cvData?.kisisel || {}), ...(localEdits?.canvas_kisisel || {}) };
    const adSoyad = (baseKis.ad_soyad || '').split(' ');
    return {
      ad: adSoyad.slice(0, -1).join(' ') || adSoyad[0] || '',
      soyad: adSoyad.length > 1 ? adSoyad[adSoyad.length - 1] : '',
      dogum_tarihi: baseKis.dogum_tarihi || '',
      telefon: baseKis.telefon || '',
      eposta: baseKis.eposta || '',
      cinsiyet: baseKis.cinsiyet || '',
      boy_cm: baseKis.boy_cm ?? '',
      kilo_kg: baseKis.kilo_kg ?? '',
      sehir: baseKis.sehir || '',
      pasaport_var: baseKis.pasaport_var ?? false,
      seyahat_engeli: baseKis.seyahat_engeli ?? false,
      seyahat_engeli_aciklama: baseKis.seyahat_engeli_aciklama || '',
      adli_sicil: baseKis.adli_sicil ?? false,
      adli_sicil_aciklama: baseKis.adli_sicil_aciklama || '',
      onceki_havayolu_basvurusu: baseKis.onceki_havayolu_basvurusu ?? false,
      mulakate_girdi_mi: baseKis.mulakate_girdi_mi ?? false,
      elendigi_asama: baseKis.elendigi_asama || '',
      instagram: baseKis.sosyal_medya?.instagram || '',
      linkedin: baseKis.sosyal_medya?.linkedin || '',
    };
  })(), mode: 'onBlur' });

  // Bulgu 3: egitim — merge canvas_egitim overrides over cvData.egitim
  const mobileForm2 = useForm({ resolver: zodResolver(step2Schema), defaultValues: (() => {
    const baseEg = { ...(cvData?.egitim || {}), ...(localEdits?.canvas_egitim || {}) };
    return {
      mezuniyet_durumu: baseEg.mezuniyet_durumu || '',
      okul: baseEg.okul || '',
      bolum: baseEg.bolum || '',
      mezuniyet_yili: baseEg.mezuniyet_yili ?? '',
      devam_eden_egitim: baseEg.devam_eden_egitim ?? false,
      yabanci_dil_egitimi: baseEg.yabanci_dil_egitimi || '',
      kabin_memurlugu_egitimi: baseEg['kabin_memurluğu_egitimi'] ?? false,
      ilk_yardim_egitimi: baseEg.ilk_yardim_egitimi ?? false,
      diksiyon_egitimi: baseEg.diksiyon_egitimi ?? false,
    };
  })(), mode: 'onBlur' });

  const mobileForm4 = useForm({ resolver: zodResolver(step4Schema), defaultValues: (() => {
    // canvas_dil override merge — ingilizce_seviye canvas duzenleme mobil sheet'te gozuksun
    const dl = { ...(cvData?.dil || {}), ...(localEdits?.canvas_dil || {}) };
    return {
      ingilizce_seviye: dl.ingilizce_seviye || '',
      konusma_pratigi: dl.konusma_pratigi || '',
      ingilizce_mulakat_tecrubesi: dl.ingilizce_mulakat_tecrubesi || '',
      ikinci_dil: dl.ikinci_dil || '',
      bilinen_diller_text: (dl.bilinen_diller || []).join(', '),
    };
  })(), mode: 'onBlur' });

  const mobileForm6 = useForm({ resolver: zodResolver(step6Schema), defaultValues: {
    unvan: (localEdits?.unvan !== undefined ? localEdits.unvan : cvData?.unvan) || '',
    hakkimda: (localEdits?.hakkimda !== undefined ? localEdits.hakkimda : cvData?.hakkimda) || '',
  }, mode: 'onBlur' });

  // Bulgu 3: dil.diller — seed from canvasDilDiller (single source of truth)
  const [mobileDilList, setMobileDilList] = useState(() =>
    (canvasDilDiller || []).map((d) => ({ dil: d.dil || '', seviye: d.seviye || '' }))
  );

  // Faz 7 — application_info section local state (for mobile sheet)
  const [mobileAiData, setMobileAiData] = useState(() => ({ ...(localEdits?.application_info || {}) }));
  // Sertifikalar local state — canvasSertifikalar'dan seed; save'de canvas_sertifikalar olarak sync edilir (tek kaynak)
  const [mobileSertList, setMobileSertList] = useState(() => (canvasSertifikalar || []).slice());

  const handleSave = async () => {
    if (section === 'kisisel') {
      const valid = await mobileForm1.trigger();
      if (!valid) return;
      onSave('kisisel_form', mobileForm1.getValues());
    } else if (section === 'egitim') {
      onSave('egitim_form', mobileForm2.getValues());
      // Sertifikalar tek kaynak: canvas_sertifikalar olarak sync et
      onSave('canvas_sertifikalar', mobileSertList.filter(Boolean));
    } else if (section === 'dil') {
      // Save both ingilizce fields AND the diller list
      onSave('dil_form', mobileForm4.getValues());
      onSave('canvas_diller', mobileDilList);
    } else if (section === 'hakkimda') {
      const valid = await mobileForm6.trigger();
      if (!valid) return;
      onSave('hakkimda_form', mobileForm6.getValues());
    } else if (section === 'is_deneyimi') {
      onSave('is_deneyimi_form', isDeneyimleri);
    } else if (section === 'application_info') {
      onSave('application_info', mobileAiData);
    }
    onClose();
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg rounded-2xl border-2 border-gray-200 shadow-[4px_4px_0_#E5E7EB] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-black text-gray-900">
            {t('cv_wizard.edit.mobile_edit_btn')}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-2">
          {section === 'kisisel' && <Step1Form form={mobileForm1} t={t} />}
          {section === 'egitim' && (
            <div className="space-y-4">
              <Step2Form form={mobileForm2} t={t} />
              {/* Sertifikalar — canvasSertifikalar tek kaynak; serbest metin yerine ekle/sil listesi */}
              <div className="border-t border-gray-100 pt-3 space-y-2">
                <p className="text-xs font-black text-gray-600 uppercase tracking-wide">
                  {t('cv.field.sertifikalar')}
                </p>
                {mobileSertList.map((s, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={s}
                      onChange={(e) => setMobileSertList((prev) => prev.map((x, xi) => xi === i ? e.target.value : x))}
                      placeholder={t('cv_wizard.canvas.placeholder_sertifika')}
                      className="flex-1 px-3 py-2 rounded-xl border-2 border-gray-200 text-sm focus:border-sky-400 focus:outline-none min-h-[40px]"
                    />
                    <button
                      type="button"
                      aria-label={t('cv.btn.sil') || 'Sil'}
                      onClick={() => setMobileSertList((prev) => prev.filter((_, xi) => xi !== i))}
                      className="p-2 text-rose-400 hover:text-rose-600 flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setMobileSertList((prev) => [...prev, ''])}
                  className="flex items-center gap-1.5 text-xs text-sky-700 border border-dashed border-sky-300 rounded-lg px-3 py-2 hover:bg-sky-50 transition-colors w-full justify-center"
                >
                  <Plus size={12} aria-hidden="true" />
                  {t('cv_wizard.canvas.add_sertifika')}
                </button>
              </div>
            </div>
          )}
          {section === 'dil' && (
            <div className="space-y-4">
              <Step4Form form={mobileForm4} t={t} />
              {/* Diller listesi — canvasDilDiller'dan seeded */}
              <div className="border-t border-gray-100 pt-3 space-y-2">
                <p className="text-xs font-black text-gray-600 uppercase tracking-wide">{t('cv_wizard.edit.section.dil')}</p>
                {mobileDilList.map((d, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={d.dil}
                      onChange={(e) => setMobileDilList((prev) => prev.map((x, xi) => xi === i ? { ...x, dil: e.target.value } : x))}
                      placeholder={t('cv_wizard.canvas.placeholder_dil_adi')}
                      className="flex-1 px-3 py-2 rounded-xl border-2 border-gray-200 text-sm focus:border-sky-400 focus:outline-none min-h-[40px]"
                    />
                    <input
                      type="text"
                      value={d.seviye}
                      onChange={(e) => setMobileDilList((prev) => prev.map((x, xi) => xi === i ? { ...x, seviye: e.target.value } : x))}
                      placeholder={t('cv_wizard.canvas.placeholder_dil_seviye')}
                      className="w-24 px-3 py-2 rounded-xl border-2 border-gray-200 text-sm focus:border-sky-400 focus:outline-none min-h-[40px]"
                    />
                    <button
                      type="button"
                      aria-label={t('cv.btn.dil_sil')}
                      onClick={() => setMobileDilList((prev) => prev.filter((_, xi) => xi !== i))}
                      className="p-2 text-rose-400 hover:text-rose-600 flex-shrink-0"
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setMobileDilList((prev) => [...prev, { dil: '', seviye: '' }])}
                  className="flex items-center gap-1.5 text-xs text-sky-700 border border-dashed border-sky-300 rounded-lg px-3 py-2 hover:bg-sky-50 transition-colors w-full justify-center"
                >
                  <Plus size={12} aria-hidden="true" />
                  {t('cv_wizard.canvas.add_dil')}
                </button>
              </div>
            </div>
          )}
          {section === 'hakkimda' && <Step6Form form={mobileForm6} t={t} isDeneyimleri={[]} onIsDeneyimleriChange={() => {}} fotoUrl={null} onFotoUpload={() => {}} onFotoDelete={() => {}} fotoUploading={false} fotoDeleting={false} />}
          {section === 'is_deneyimi' && (
            <IsDeneyimiRepeater
              entries={isDeneyimleri}
              onChange={onIsDeneyimleriChange}
              t={t}
            />
          )}
          {/* Faz 7 — application_info section in mobile sheet */}
          {section === 'application_info' && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 bg-amber-50 border-2 border-amber-200 rounded-xl">
                <Info size={14} className="text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-xs text-amber-700 leading-relaxed">
                  {t('cv_wizard.application_info.motivation_note')}
                </p>
              </div>
              <MotivasyonForm formData={mobileAiData} onChange={setMobileAiData} t={t} />
            </div>
          )}
        </div>
        <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleSave}
            className="flex-1 py-2.5 px-4 bg-sky-600 text-white font-bold rounded-xl border-2 border-sky-700 shadow-[2px_2px_0_#0369a1] hover:bg-sky-700 transition-colors min-h-[44px]"
          >
            {t('cv_wizard.edit.mobile_sheet_save') || 'Kaydet'}
          </motion.button>
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={onClose}
            className="py-2.5 px-4 bg-white text-gray-700 font-bold rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-colors min-h-[44px]"
          >
            {t('cv_wizard.edit.mobile_sheet_close')}
          </motion.button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Faz 6 — EditStep (mobile tab bar + template toolbar + Bulgu 1+2 sidebar fix)
// ─────────────────────────────────────────────────────────────────────────────

function EditStep({
  cvData, isDeneyimleri, selectionState, localEdits,
  onFieldSave, onIsDeneyimleriChange, t, autosaveStatus,
  onHasActiveEditChange, fotoBlobUrl,
  // Faz 5 canvas-edit props
  canvasDilDiller,
  onDeneyimAdd, onDeneyimDelete, onDeneyimMaddeAdd, onDeneyimMaddeDelete,
  onDilAdd, onDilDelete,
  // Faz 6 template props
  templateId, onTemplateChange,
  // Faz 7 sertifika + application_info props
  canvasSertifikalar,
  onSertifikaAdd, onSertifikaDelete,
  // Faz 7 section toggle
  onSectionToggle,
  // Foto upload/delete (canvas photo circle)
  onFotoUpload, onFotoDelete, fotoUploading, fotoDeleting,
}) {
  const [mobileSheet, setMobileSheet] = useState(null);
  // Faz 7 — ApplicationInfoDialog state
  const [appInfoOpen, setAppInfoOpen] = useState(false);
  // Faz 6: mobile preview|edit tab state (replaces matchMedia isMobile)
  const [mobileActiveTab, setMobileActiveTab] = useState('preview');
  const mobileTabListRef = useRef(null);

  const MOBILE_TABS = ['preview', 'edit'];

  const handleMobileTabKey = (e, idx) => {
    let newIdx = idx;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      newIdx = (idx + 1) % MOBILE_TABS.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      newIdx = (idx - 1 + MOBILE_TABS.length) % MOBILE_TABS.length;
    } else { return; }
    setMobileActiveTab(MOBILE_TABS[newIdx]);
    const tabs = mobileTabListRef.current?.querySelectorAll('[role="tab"]');
    tabs?.[newIdx]?.focus();
  };


  const { includedSections = {} } = selectionState || {};

  // Bulgu 2 fix: merge canvas_kisisel/canvas_egitim overrides for sidebar display
  const mergedKisisel = { ...(cvData?.kisisel || {}), ...(localEdits?.canvas_kisisel || {}) };
  const mergedEgitim  = { ...(cvData?.egitim  || {}), ...(localEdits?.canvas_egitim  || {}) };

  const unvanValue = localEdits?.unvan !== undefined ? localEdits.unvan : (cvData?.unvan || '');
  const hakkimdaValue = localEdits?.hakkimda !== undefined ? localEdits.hakkimda : (cvData?.hakkimda || '');

  const handleMobileSectionSave = (sectionKey, data) => {
    onFieldSave(sectionKey, data);
  };

  const canvasEditProps = onFieldSave ? {
    localEdits,
    onFieldSave,
    canvasDilDiller: canvasDilDiller || [],
    onDeneyimAdd,
    onDeneyimDelete,
    onDeneyimMaddeAdd,
    onDeneyimMaddeDelete,
    onDilAdd,
    onDilDelete,
    onHasActiveEditChange,
    // Faz 7 — sertifikalar
    canvasSertifikalar: canvasSertifikalar || [],
    onSertifikaAdd,
    onSertifikaDelete,
    // Faz 7 — section toggle (Eye/EyeOff)
    onSectionToggle,
    // Foto upload/delete — onFotoClick receives a File object from CanvasFotoCircle
    onFotoClick: onFotoUpload,
    onFotoDelete,
    fotoUploading,
    fotoDeleting,
  } : undefined;

  const SectionEditBtn = ({ section }) => (
    <motion.button
      type="button"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.97 }}
      onClick={() => setMobileSheet(section)}
      className="flex items-center gap-1.5 mt-2 px-3 py-1.5 text-xs font-bold text-sky-700 border-2 border-sky-200 rounded-lg hover:bg-sky-50 transition-colors min-h-[36px]"
    >
      <Pencil size={12} aria-hidden="true" />
      {t('cv_wizard.edit.mobile_edit_btn')}
    </motion.button>
  );

  return (
    <div className="space-y-4">
      {/* Faz 6 GÖREV 1: Template Toolbar — sticky segmented control */}
      <TemplateToolbar
        templateId={templateId}
        onTemplateChange={onTemplateChange}
        autosaveStatus={autosaveStatus}
        t={t}
        onAppInfoClick={onFieldSave ? () => setAppInfoOpen(true) : undefined}
        appInfoOpen={appInfoOpen}
      />
      {/* Faz 7 — ApplicationInfoDialog */}
      {appInfoOpen && (
        <ApplicationInfoDialog
          open={appInfoOpen}
          onClose={() => setAppInfoOpen(false)}
          cvData={cvData}
          localEdits={localEdits}
          onSave={onFieldSave}
          t={t}
        />
      )}

      {/* Faz 6 GÖREV 3: Mobile tab bar (< lg) */}
      <div
        ref={mobileTabListRef}
        role="tablist"
        aria-label={t('cv_wizard.canvas.mobile_tabs_label')}
        className="lg:hidden flex bg-gray-100 rounded-xl p-0.5 gap-0.5"
      >
        {MOBILE_TABS.map((tab, idx) => {
          const isSelected = tab === mobileActiveTab;
          return (
            <button
              key={tab}
              role="tab"
              aria-selected={isSelected}
              tabIndex={isSelected ? 0 : -1}
              onClick={() => setMobileActiveTab(tab)}
              onKeyDown={(e) => handleMobileTabKey(e, idx)}
              className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all focus-visible:outline-2 focus-visible:outline-sky-500 focus-visible:outline-offset-1 min-h-[40px] ${
                isSelected
                  ? 'bg-white shadow-sm text-sky-700 border border-sky-200'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'preview' ? t('cv_wizard.canvas.mobile_preview_tab') : t('cv_wizard.canvas.mobile_edit_tab')}
            </button>
          );
        })}
      </div>

      {/* Desktop A4 Canvas (full inline-edit) */}
      <div className="hidden lg:block">
        <CVCanvas
          cvData={cvData}
          selectionState={selectionState}
          fotoBlobUrl={fotoBlobUrl}
          isDeneyimleri={isDeneyimleri}
          t={t}
          canvasEdit={canvasEditProps}
          templateId={templateId}
        />
      </div>

      {/* Mobile Önizleme tab: read-only canvas */}
      {mobileActiveTab === 'preview' && (
        <div className="lg:hidden">
          <CVCanvas
            cvData={cvData}
            selectionState={selectionState}
            fotoBlobUrl={fotoBlobUrl}
            isDeneyimleri={isDeneyimleri}
            t={t}
            canvasEdit={undefined}
            templateId={templateId}
          />
        </div>
      )}

      {/* Mobile Düzenle tab: section list with pencil buttons */}
      {mobileActiveTab === 'edit' && (
        <div className="lg:hidden space-y-3">
          {/* Personal section */}
          <div className="border-2 border-gray-100 rounded-xl p-4 space-y-2">
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-wide mb-2">
              {t('cv_wizard.edit.section.kisisel')}
            </p>
            <span className="text-base font-black text-gray-900">{mergedKisisel.ad_soyad || '—'}</span>
            <div>
              <span className="text-[11px] text-gray-500 uppercase tracking-wide">{t('cv_wizard.edit.field.unvan')}</span>
              <p className="text-sm text-gray-700 font-medium mt-0.5">{unvanValue || <em className="text-gray-400 not-italic">{t('cv_wizard.edit.empty_field')}</em>}</p>
            </div>
            <SectionEditBtn section="kisisel" />
          </div>

          {/* Hakkımda */}
          {includedSections.hakkimda && (
            <div className="border-2 border-gray-100 rounded-xl p-4">
              <p className="text-[10px] font-black text-gray-500 uppercase tracking-wide mb-2">
                {t('cv_wizard.edit.section.hakkimda')}
              </p>
              <p className="text-sm text-gray-700 leading-relaxed line-clamp-3">
                {hakkimdaValue || <em className="text-gray-400 not-italic">{t('cv_wizard.edit.empty_field')}</em>}
              </p>
              <SectionEditBtn section="hakkimda" />
            </div>
          )}

          {/* İş Deneyimleri */}
          {includedSections.is_deneyimleri && (
            <div className="border-2 border-gray-100 rounded-xl p-4">
              <p className="text-[10px] font-black text-gray-500 uppercase tracking-wide mb-2">
                {t('cv_wizard.edit.section.is_deneyimi')}
              </p>
              {isDeneyimleri.filter((_, i) => selectionState?.isDeneyimleriInclude?.[i] !== false).map((e, i) => (
                <div key={i} className="flex justify-between items-start gap-2 py-1.5 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="text-sm font-bold text-gray-800">{e.pozisyon || '—'}</p>
                    <p className="text-xs text-gray-500">{e.sirket}</p>
                  </div>
                  <p className="text-[11px] text-gray-500 whitespace-nowrap flex-shrink-0">
                    {[e.baslangic, e.devam_ediyor ? '…' : e.bitis].filter(Boolean).join(' – ')}
                  </p>
                </div>
              ))}
              <SectionEditBtn section="is_deneyimi" />
            </div>
          )}

          {/* Eğitim — Bulgu 2 fix: mergedEgitim */}
          {includedSections.egitim && (
            <div className="border-2 border-gray-100 rounded-xl p-4">
              <p className="text-[10px] font-black text-gray-500 uppercase tracking-wide mb-2">
                {t('cv_wizard.edit.section.egitim')}
              </p>
              <p className="text-sm font-bold text-gray-800">{mergedEgitim.okul || '—'}</p>
              <p className="text-xs text-gray-500">{mergedEgitim.bolum}{mergedEgitim.mezuniyet_yili ? ` · ${mergedEgitim.mezuniyet_yili}` : ''}</p>
              <SectionEditBtn section="egitim" />
            </div>
          )}

          {/* Diller — Bulgu 1 fix: canvasDilDiller */}
          {includedSections.dil && (
            <div className="border-2 border-gray-100 rounded-xl p-4">
              <p className="text-[10px] font-black text-gray-500 uppercase tracking-wide mb-2">
                {t('cv_wizard.edit.section.dil')}
              </p>
              {cvData?.dil?.ingilizce_seviye && (
                <p className="text-sm text-gray-700">İngilizce · {cvData.dil.ingilizce_seviye}</p>
              )}
              {(canvasDilDiller || [])
                .filter((_, i) => selectionState?.dillerInclude?.[i] !== false)
                .map((d, i) => (
                  <p key={i} className="text-sm text-gray-700">{[d.dil, d.seviye].filter(Boolean).join(' · ')}</p>
                ))}
              <SectionEditBtn section="dil" />
            </div>
          )}

          {/* Faz 7 — Başvuru Bilgileri row in mobile edit tab */}
          {onFieldSave && (
            <div className="border-2 border-amber-100 bg-amber-50 rounded-xl p-4">
              <p className="text-[10px] font-black text-amber-600 uppercase tracking-wide mb-1">
                {t('cv_wizard.application_info.mobile_section_label')}
              </p>
              <p className="text-xs text-amber-700 mb-2">{t('cv_wizard.application_info.motivation_note')}</p>
              <SectionEditBtn section="application_info" />
            </div>
          )}
        </div>
      )}

      {/* Mobile bottom-sheet modal — Bulgu 3 fix: passes localEdits + canvasDilDiller */}
      {mobileSheet && (
        <MobileEditSheet
          section={mobileSheet}
          cvData={cvData}
          localEdits={localEdits}
          canvasDilDiller={canvasDilDiller}
          t={t}
          onClose={() => setMobileSheet(null)}
          onSave={handleMobileSectionSave}
          isDeneyimleri={isDeneyimleri}
          onIsDeneyimleriChange={onIsDeneyimleriChange}
          canvasSertifikalar={canvasSertifikalar}
          onSertifikaAdd={onSertifikaAdd}
          onSertifikaDelete={onSertifikaDelete}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Faz 7 — Save Adımı (wizard step index 2)
// Özet önizleme + 2 PDF varyant + Eğitmene Gönder modalı
// ─────────────────────────────────────────────────────────────────────────────

function SaveStep({ cvData, selectionState, localEdits, isDeneyimleri, templateId, t, lang, onSubmit, submitting, cvStatus, canvasDilDiller }) {
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingAts, setDownloadingAts] = useState(false);
  const toast = useToast();

  const isApproved = cvStatus === 'approved';
  const includedSections = selectionState?.includedSections || {};
  // Canvas override merge — ozet onizleme canvas duzenlelerini yansitsin
  const kisisel = { ...(cvData?.kisisel || {}), ...(localEdits?.canvas_kisisel || {}) };
  const egitim  = { ...(cvData?.egitim  || {}), ...(localEdits?.canvas_egitim  || {}) };
  const unvan = localEdits?.unvan !== undefined ? localEdits.unvan : (cvData?.unvan || '');
  const hakkimda = localEdits?.hakkimda !== undefined ? localEdits.hakkimda : (cvData?.hakkimda || '');
  const displayedDen = (isDeneyimleri || []).filter((_, i) => selectionState?.isDeneyimleriInclude?.[i] !== false);
  const displayedDil = (canvasDilDiller || []).filter((_, i) => selectionState?.dillerInclude?.[i] !== false);

  const sectionCount = 1 +
    (includedSections.is_deneyimleri ? 1 : 0) +
    (includedSections.egitim ? 1 : 0) +
    (includedSections.dil ? 1 : 0) +
    (includedSections.hakkimda ? 1 : 0) +
    (includedSections.referanslar ? 1 : 0);

  const selectedNames = [
    t('cv_wizard.select.section.kisisel'),
    ...(includedSections.is_deneyimleri ? [t('cv_wizard.select.section.is_deneyimi')] : []),
    ...(includedSections.egitim ? [t('cv_wizard.select.section.egitim')] : []),
    ...(includedSections.dil ? [t('cv_wizard.select.section.dil')] : []),
    ...(includedSections.hakkimda ? [t('cv_wizard.select.section.hakkimda')] : []),
    ...(includedSections.referanslar ? [t('cv_wizard.select.section.referanslar')] : []),
  ];

  const downloadPDF = async (variant) => {
    const isAts = variant === 'no_photo';
    if (isAts) setDownloadingAts(true); else setDownloading(true);
    try {
      const token = getToken();
      const url = isAts ? `${API_URL}/cabin/cv/pdf?variant=no_photo` : `${API_URL}/cabin/cv/pdf`;
      const res = await axios.get(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: 'blob',
      });
      // Güvenlik: gerçek PDF mi geliyor? Aksi hâlde blob JSON hata olabilir.
      const contentType = res.headers?.['content-type'] || '';
      if (!contentType.includes('application/pdf')) {
        let msg = t('cv.status.approved.download_error');
        try {
          const text = await res.data.text();
          const json = JSON.parse(text);
          const detail = json?.detail;
          if (typeof detail === 'object') {
            msg = (lang === 'tr' ? detail.message_tr : detail.message_en) || msg;
          } else if (typeof detail === 'string') {
            msg = detail;
          }
        } catch { /* fallback mesaj */ }
        toast.error(msg);
        return;
      }
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = isAts ? 'cv-ats.pdf' : 'cv-onaylandi.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const errStatus = err?.response?.status;
      if (errStatus === 401 || errStatus === 403) {
        toast.error(lang === 'tr' ? 'Oturum süresi doldu. Lütfen tekrar giriş yapın.' : 'Session expired. Please log in again.');
      } else {
        let msg = t('cv.status.approved.download_error');
        try {
          const blob = err?.response?.data;
          if (blob instanceof Blob) {
            const text = await blob.text();
            const json = JSON.parse(text);
            const detail = json?.detail;
            if (typeof detail === 'object') {
              msg = (lang === 'tr' ? detail.message_tr : detail.message_en) || msg;
            } else if (typeof detail === 'string') {
              msg = detail;
            }
          }
        } catch { /* fallback mesaj kullanılır */ }
        toast.error(msg);
      }
    } finally {
      if (isAts) setDownloadingAts(false); else setDownloading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Özet önizleme kartı */}
      <div className="border-2 border-gray-100 rounded-xl p-4 space-y-4 bg-white shadow-[2px_2px_0_#E5E7EB]">
        <p className="text-xs font-black text-gray-500 uppercase tracking-wide">
          {t('cv_wizard.save.preview_title')}
        </p>

        {/* İsim + unvan + şablon */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-display text-lg font-black text-gray-900 leading-tight">
              {kisisel.ad_soyad || '—'}
            </p>
            {unvan && <p className="text-sm text-sky-600 font-semibold mt-0.5">{unvan}</p>}
            <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
              {kisisel.eposta && <span>{kisisel.eposta}</span>}
              {kisisel.telefon && <span>{kisisel.telefon}</span>}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
              {t('cv_wizard.save.template_label')}
            </p>
            <span className="text-sm font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded-lg px-2 py-0.5">
              {t(`cv_wizard.template.names.${templateId || 'classic'}`)}
            </span>
          </div>
        </div>

        {/* Hakkımda */}
        {includedSections.hakkimda && hakkimda && (
          <div>
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-wide mb-1">
              {t('cv_wizard.edit.section.hakkimda')}
            </p>
            <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">{hakkimda}</p>
          </div>
        )}

        {/* İş deneyimleri */}
        {includedSections.is_deneyimleri && displayedDen.length > 0 && (
          <div>
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-wide mb-1">
              {t('cv_wizard.edit.section.is_deneyimi')}
            </p>
            {displayedDen.slice(0, 2).map((e, i) => (
              <div key={i} className="flex justify-between items-start gap-2 py-0.5">
                <p className="text-xs font-bold text-gray-800">
                  {e.pozisyon || '—'}
                  {e.sirket && <span className="text-gray-500 font-normal"> · {e.sirket}</span>}
                </p>
                <p className="text-[11px] text-gray-500 whitespace-nowrap flex-shrink-0">
                  {[e.baslangic, e.devam_ediyor ? '…' : e.bitis].filter(Boolean).join(' – ')}
                </p>
              </div>
            ))}
            {displayedDen.length > 2 && (
              <p className="text-[11px] text-gray-500 mt-0.5">+{displayedDen.length - 2}</p>
            )}
          </div>
        )}

        {/* Eğitim */}
        {includedSections.egitim && (egitim.okul || egitim.bolum) && (
          <div>
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-wide mb-1">
              {t('cv_wizard.edit.section.egitim')}
            </p>
            <p className="text-xs font-bold text-gray-800">{egitim.okul}</p>
            <p className="text-xs text-gray-500">
              {egitim.bolum}{egitim.mezuniyet_yili ? ` · ${egitim.mezuniyet_yili}` : ''}
            </p>
          </div>
        )}

        {/* Diller */}
        {includedSections.dil && (displayedDil.length > 0 || cvData?.dil?.ingilizce_seviye) && (
          <div>
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-wide mb-1">
              {t('cv_wizard.edit.section.dil')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {cvData?.dil?.ingilizce_seviye && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-md">
                  İngilizce · {cvData.dil.ingilizce_seviye}
                </span>
              )}
              {displayedDil.map((d, i) => (
                <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-md">
                  {[d.dil, d.seviye].filter(Boolean).join(' · ')}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Seçili bölüm etiketleri */}
        <div className="pt-3 border-t border-gray-100">
          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1.5">
            {interpolateStr(t('cv_wizard.save.selected_sections'), { n: sectionCount })}
          </p>
          <div className="flex flex-wrap gap-1">
            {selectedNames.map((name) => (
              <span
                key={name}
                className="px-1.5 py-0.5 bg-sky-50 border border-sky-200 text-sky-700 text-[11px] font-bold rounded"
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* PDF İndirme butonları */}
      <div className="space-y-2">
        <motion.button
          whileHover={{ scale: isApproved && !downloading && !downloadingAts ? 1.02 : 1 }}
          whileTap={{ scale: isApproved ? 0.97 : 1 }}
          type="button"
          onClick={isApproved ? () => downloadPDF('photo') : undefined}
          disabled={!isApproved || downloading || downloadingAts}
          className={`w-full flex items-center justify-center gap-2 py-3 px-5 rounded-xl border-2 font-bold text-sm transition-colors min-h-[44px]
            ${isApproved
              ? 'bg-emerald-600 text-white border-emerald-700 shadow-[2px_2px_0_#047857] hover:bg-emerald-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed'
              : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
            }`}
          aria-disabled={!isApproved || undefined}
        >
          {downloading
            ? <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            : <Download size={16} aria-hidden="true" />}
          {isApproved
            ? (downloading ? t('cv.pdf.downloading') : t('cv_wizard.save.pdf_photo'))
            : t('cv_wizard.save.pdf_disabled')}
        </motion.button>

        <motion.button
          whileHover={{ scale: isApproved && !downloading && !downloadingAts ? 1.02 : 1 }}
          whileTap={{ scale: isApproved ? 0.97 : 1 }}
          type="button"
          onClick={isApproved ? () => downloadPDF('no_photo') : undefined}
          disabled={!isApproved || downloading || downloadingAts}
          className={`w-full flex items-center justify-center gap-2 py-3 px-5 rounded-xl border-2 font-bold text-sm transition-colors min-h-[44px]
            ${isApproved
              ? 'bg-sky-600 text-white border-sky-700 shadow-[2px_2px_0_#0369a1] hover:bg-sky-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 disabled:opacity-60 disabled:cursor-not-allowed'
              : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
            }`}
          aria-disabled={!isApproved || undefined}
        >
          {downloadingAts
            ? <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            : <FileText size={16} aria-hidden="true" />}
          {isApproved
            ? (downloadingAts ? t('cv.pdf.downloading') : t('cv_wizard.save.pdf_no_photo'))
            : t('cv_wizard.save.pdf_disabled')}
        </motion.button>

        {!isApproved && (
          <p className="flex items-center gap-1.5 text-xs text-amber-600 font-medium mt-1" role="note">
            <Info size={13} aria-hidden="true" />
            {t('cv_wizard.save.pdf_disabled')}
          </p>
        )}
      </div>

      {/* Eğitmene Gönder butonu */}
      <motion.button
        whileHover={{ scale: !submitting ? 1.02 : 1 }}
        whileTap={{ scale: !submitting ? 0.97 : 1 }}
        type="button"
        onClick={() => setSendModalOpen(true)}
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 py-3 px-5 bg-sky-600 text-white font-bold rounded-xl border-2 border-sky-700 shadow-[2px_2px_0_#0369a1] hover:bg-sky-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 min-h-[44px]"
      >
        {submitting
          ? <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          : <MessageSquare size={16} aria-hidden="true" />}
        {submitting ? t('cv_wizard.save.sending') : t('cv_wizard.save.send_btn')}
      </motion.button>

      {/* Eğitmene Gönder Modalı */}
      <Dialog open={sendModalOpen} onOpenChange={(v) => { if (!v) setSendModalOpen(false); }}>
        <DialogContent className="max-w-sm rounded-2xl border-2 border-gray-200 shadow-[4px_4px_0_#E5E7EB]">
          <DialogHeader>
            <DialogTitle className="font-display text-lg font-black text-gray-900">
              {t('cv_wizard.save.send_modal_title')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 leading-relaxed mt-2">
            {t('cv_wizard.save.send_modal_body')}
          </p>
          <div className="flex gap-3 mt-5">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              type="button"
              onClick={() => setSendModalOpen(false)}
              className="flex-1 py-2.5 px-4 bg-white text-gray-700 font-bold rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-colors min-h-[44px]"
            >
              {t('cv_wizard.save.send_modal_cancel')}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              type="button"
              onClick={async () => {
                setSendModalOpen(false);
                await onSubmit();
              }}
              className="flex-1 py-2.5 px-4 bg-sky-600 text-white font-bold rounded-xl border-2 border-sky-700 shadow-[2px_2px_0_#0369a1] hover:bg-sky-700 transition-colors min-h-[44px]"
            >
              {t('cv_wizard.save.send_modal_confirm')}
            </motion.button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Wizard Component
// ─────────────────────────────────────────────────────────────────────────────

const STEP_ICONS = [User, FileText, CheckCircle2];

export default function CabinCVWizard() {
  const { t, lang } = useLanguage();
  const toast = useToast();
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const stepHeadingRef = useRef(null);

  // Europass 3-adım yapısı: Welcome → Düzenle → Kaydet
  const STEP_LABELS = [
    t('cv_wizard.steps.welcome'),
    t('cv_wizard.steps.edit'),
    t('cv_wizard.steps.save'),
  ];

  const STEPS = STEP_LABELS.map((label, i) => ({ id: i, label }));

  const { data: cvData, isLoading, isError, refetch } = useCabinCVCurrent();
  const saveDraftMutation = useSaveCVDraft();
  const submitMutation = useSubmitCV();
  const uploadPhotoMutation = useUploadCvPhoto();
  const deletePhotoMutation = useDeleteCvPhoto();
  const fotoUploading = uploadPhotoMutation.isPending;
  const fotoDeleting = deletePhotoMutation.isPending;

  // Per-step form data store (holds values from each step)
  const formDataRef = useRef(Array(3).fill(null));

  // is_deneyimleri: managed separately (repeater, not react-hook-form)
  const [isDeneyimleri, setIsDeneyimleri] = useState([]);
  // Ref mirror — stale closure guard for triggerAutosave setTimeout
  const isDeneyimleriRef = useRef([]);
  // canvasDilDiller: Faz 5 canvas edit için dil.diller state'i (cvData.dil.diller'dan seed edilir)
  const [canvasDilDiller, setCanvasDilDiller] = useState([]);
  const canvasDilDillerRef = useRef([]);
  // Faz 7 — canvasSertifikalar: sertifikalar list state (EditableSertifikalarList için)
  const [canvasSertifikalar, setCanvasSertifikalar] = useState([]);
  const canvasSertifikalarRef = useRef([]);
  // foto_url: returned by upload endpoint or seeded from existing CV
  const [fotoUrl, setFotoUrl] = useState(null);
  // blob preview URL for photo fetched from GET /cabin/cv/photo
  const [fotoBlobUrl, setFotoBlobUrl] = useState(null);
  // Europass Select step: which sections/records to include
  const [selectionState, setSelectionState] = useState(null);
  const selectionStateRef = useRef(null);
  // Europass Edit step: inline overrides (unvan, hakkimda + mobile section forms)
  const [localEdits, setLocalEdits] = useState({});
  const localEditsRef = useRef({});
  // Edit step unsaved-changes modal
  const [editUnsavedModal, setEditUnsavedModal] = useState(false);
  const [pendingNavStep, setPendingNavStep] = useState(null);
  // Europass Template step: seçili şablon
  const [templateId, setTemplateId] = useState('classic');
  const templateIdRef = useRef('classic');
  // Edit adımında açık InlineField var mı? (handleNext guard)
  const hasActiveEditRef = useRef(false);
  const handleEditActiveChange = useCallback((isActive) => {
    hasActiveEditRef.current = isActive;
  }, []);

  const [state, dispatch] = useReducer(wizardReducer, {
    currentStep: 0,
    completed: new Set(),
    skipped: new Set(),
    autosaveStatus: AUTOSAVE_IDLE,
    kvkkConsented: false,
    submitted: false,
  });

  // Seed from existing CV data once loaded
  const [seeded, setSeeded] = useState(false);
  // When user clicks "Edit" from changes_requested status screen
  const [forceEdit, setForceEdit] = useState(false);
  useEffect(() => {
    if (!isLoading && !seeded) {
      formDataRef.current = Array(3).fill(null);
      setSeeded(true);
      // If there's existing data, mark KVKK as consented
      if (cvData?.kvkk_consent) {
        dispatch({ type: 'SET_KVKK', value: true });
      }
      // Seed is_deneyimleri
      if (cvData?.is_deneyimleri && Array.isArray(cvData.is_deneyimleri)) {
        isDeneyimleriRef.current = cvData.is_deneyimleri;
        setIsDeneyimleri(cvData.is_deneyimleri);
      }
      // Seed foto_url
      if (cvData?.foto_url) {
        setFotoUrl(cvData.foto_url);
      }
      // Seed selectionState for Europass Select step
      const initSelection = seedSelectionState(cvData);
      setSelectionState(initSelection);
      selectionStateRef.current = initSelection;
      // Seed localEdits for Europass Edit step
      const initEdits = { unvan: cvData?.unvan, hakkimda: cvData?.hakkimda };
      setLocalEdits(initEdits);
      localEditsRef.current = initEdits;
      // Seed canvasDilDiller from cvData.dil.diller (Faz 5 canvas edit)
      const seedDilDiller = (cvData?.dil?.diller || []).map((d) => ({ dil: d.dil || '', seviye: d.seviye || '' }));
      setCanvasDilDiller(seedDilDiller);
      canvasDilDillerRef.current = seedDilDiller;
      // Faz 7 — Seed canvasSertifikalar from cvData.egitim.sertifikalar
      const seedSertifikalar = (cvData?.egitim?.sertifikalar || []).filter(Boolean);
      setCanvasSertifikalar(seedSertifikalar);
      canvasSertifikalarRef.current = seedSertifikalar;
      // Seed templateId from cvData (backend'den geliyorsa kullan, yoksa 'classic')
      const seedTemplate = cvData?.template_id || 'classic';
      setTemplateId(seedTemplate);
      templateIdRef.current = seedTemplate;
    }
  }, [isLoading, cvData, seeded]);

  // Fetch photo blob for preview when foto_url exists
  useEffect(() => {
    if (!fotoUrl) {
      setFotoBlobUrl(null);
      return;
    }
    let cancelled = false;
    const fetchPhoto = async () => {
      try {
        const token = getToken();
        const res = await axios.get(`${API_URL}/cabin/cv/photo`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: 'blob',
        });
        if (!cancelled) {
          const url = URL.createObjectURL(res.data);
          setFotoBlobUrl(url);
        }
      } catch (err) {
        // Photo not available or error — don't show preview
        console.error('CV foto blob fetch başarısız', err);
        if (!cancelled) setFotoBlobUrl(null);
      }
    };
    fetchPhoto();
    return () => {
      cancelled = true;
      setFotoBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [fotoUrl]);

  // ─── Canvas foto upload / delete handlers ─────────────────────────────────────
  const handleCanvasFotoUpload = useCallback(async (file) => {
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error(t('cv.validation.foto_format'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('cv.validation.foto_size'));
      return;
    }
    try {
      const result = await uploadPhotoMutation.mutateAsync(file);
      setFotoUrl(result.foto_url);
    } catch (err) {
      if (err?.response?.status === 401) { navigate('/login'); return; }
      toast.error(t('cv.error.foto_upload_failed'));
    }
  }, [uploadPhotoMutation, navigate, toast, t]);

  const handleCanvasFotoDelete = useCallback(async () => {
    try {
      await deletePhotoMutation.mutateAsync();
      setFotoUrl(null);
    } catch (err) {
      if (err?.response?.status === 401) { navigate('/login'); return; }
      toast.error(t('cv.error.foto_delete_failed'));
    }
  }, [deletePhotoMutation, navigate, toast, t]);

  // KVKK artık modal değil, Welcome adımında inline card olarak gösterilir.
  // handleKVKKInline: WelcomeStep'ten gelen onKvkkChange callback'i
  const handleKVKKInline = (checked) => {
    dispatch({ type: 'SET_KVKK', value: checked });
  };

  // Current step's form — Europass wizard kullanır EUROPASS_STEP_SCHEMAS (boş schema, placeholder adımlar için)
  const form = useForm({
    resolver: zodResolver(EUROPASS_STEP_SCHEMAS[state.currentStep]),
    defaultValues: formDataRef.current[state.currentStep] || {},
    mode: 'onBlur',
  });

  // Re-initialize form when step changes
  useEffect(() => {
    if (seeded) {
      form.reset(formDataRef.current[state.currentStep] || {});
      // Focus step heading for accessibility
      setTimeout(() => {
        stepHeadingRef.current?.focus();
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentStep, seeded]);

  // ─── Autosave ───────────────────────────────────────────────────────────────
  const autosaveTimerRef = useRef(null);

  const triggerAutosave = useCallback(
    (_stepData) => {
      if (!state.kvkkConsented) return; // Don't autosave before KVKK
      clearTimeout(autosaveTimerRef.current);
      dispatch({ type: 'SET_AUTOSAVE', status: AUTOSAVE_SAVING });
      autosaveTimerRef.current = setTimeout(async () => {
        try {
          // Europass wizard: build body from cvData + local overrides + selection
          // Tüm ref'ler stale-closure-safe (her değişimde ref.current güncellenir)
          const body = buildEuropassBody(
            cvData,
            localEditsRef.current,
            isDeneyimleriRef.current,
            selectionStateRef.current,
            true,
            fotoUrl,
            templateIdRef.current,
            canvasDilDillerRef.current,
            canvasSertifikalarRef.current,
          );
          await saveDraftMutation.mutateAsync(body);
          dispatch({ type: 'SET_AUTOSAVE', status: AUTOSAVE_SAVED });
          setTimeout(() => dispatch({ type: 'SET_AUTOSAVE', status: AUTOSAVE_IDLE }), 2500);
        } catch {
          dispatch({ type: 'SET_AUTOSAVE', status: AUTOSAVE_ERROR });
          setTimeout(() => dispatch({ type: 'SET_AUTOSAVE', status: AUTOSAVE_IDLE }), 3000);
        }
      }, 1500);
    },
    // isDeneyimleri intentionally omitted — read via isDeneyimleriRef.current (stale-closure guard)
     
    [state.kvkkConsented, saveDraftMutation, cvData, fotoUrl]
  );

  // Watch form changes for autosave (no-op for Europass wizard — empty schemas)
  useEffect(() => {
    const subscription = form.watch((_values) => {
      // Europass wizard: autosave triggered manually via triggerAutosave()
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // ─── Faz 7: canvas bölüm toggle (Eye/EyeOff) ────────────────────────────────
  const handleCanvasSectionToggle = useCallback((section, included) => {
    setSelectionState((prev) => {
      const updated = {
        ...prev,
        includedSections: { ...(prev?.includedSections || {}), [section]: included },
      };
      selectionStateRef.current = updated;
      return updated;
    });
    triggerAutosave();
  }, [triggerAutosave]);

  // ─── Europass Template: şablon seçim handler ─────────────────────────────────
  const handleTemplateChange = useCallback((id) => {
    setTemplateId(id);
    templateIdRef.current = id;
    triggerAutosave();
  }, [triggerAutosave]);

  // ─── Europass Edit: inline field save handler ────────────────────────────────
  // Supports nested path IDs:
  //   'is_deneyimi.{idx}.{field}'           → isDeneyimleri[idx][field]
  //   'is_deneyimi.{idx}.maddeler.{mi}'     → isDeneyimleri[idx].maddeler[mi]
  //   'dil.diller.{idx}.{field}'            → canvasDilDiller[idx][field]
  //   'canvas.kisisel.{field}'              → localEdits.canvas_kisisel[field]
  //   'canvas.egitim.{field}'               → localEdits.canvas_egitim[field]
  const handleFieldSave = useCallback((fieldId, value) => {
    // ── Path: is_deneyimi.{idx}.* ────────────────────────────────────────────
    if (fieldId.startsWith('is_deneyimi.')) {
      const parts = fieldId.split('.');
      const entryIdx = parseInt(parts[1], 10);
      if (!isNaN(entryIdx)) {
        const newList = isDeneyimleriRef.current.map((entry, i) => {
          if (i !== entryIdx) return entry;
          if (parts[2] === 'maddeler' && parts[3] !== undefined) {
            const mi = parseInt(parts[3], 10);
            // Bug #5 fix: NaN guard — skip malformed index
            if (!isNaN(mi)) {
              const maddeler = [...(entry.maddeler || [])];
              maddeler[mi] = value;
              return { ...entry, maddeler };
            }
            return entry;
          }
          return { ...entry, [parts[2]]: value };
        });
        isDeneyimleriRef.current = newList;
        setIsDeneyimleri(newList);
        triggerAutosave();
        return;
      }
    }

    // ── Path: dil.diller.{idx}.{field} ───────────────────────────────────────
    if (fieldId.startsWith('dil.diller.')) {
      const parts = fieldId.split('.');
      const dilIdx = parseInt(parts[2], 10);
      const field = parts[3];
      if (!isNaN(dilIdx) && field) {
        const newList = canvasDilDillerRef.current.map((d, i) =>
          i === dilIdx ? { ...d, [field]: value } : d
        );
        canvasDilDillerRef.current = newList;
        setCanvasDilDiller(newList);
        triggerAutosave();
        return;
      }
    }

    // ── Path: canvas.kisisel.{field} or canvas.egitim.{field} ────────────────
    if (fieldId.startsWith('canvas.kisisel.') || fieldId.startsWith('canvas.egitim.')) {
      const parts = fieldId.split('.');
      // parts[0]='canvas', parts[1]='kisisel'|'egitim', parts[2]=field
      const stateKey = `canvas_${parts[1]}`; // 'canvas_kisisel' or 'canvas_egitim'
      const field = parts[2];
      const newSection = { ...(localEditsRef.current[stateKey] || {}), [field]: value };
      const newEdits = { ...localEditsRef.current, [stateKey]: newSection };
      localEditsRef.current = newEdits;
      setLocalEdits(newEdits);
      // Auto-include egitim section when a field is populated for the first time
      if (fieldId.startsWith('canvas.egitim.') && value) {
        const curSel = selectionStateRef.current || {};
        if (!curSel.includedSections?.egitim) {
          const newSel = {
            ...curSel,
            includedSections: { ...(curSel.includedSections || {}), egitim: true },
          };
          selectionStateRef.current = newSel;
          setSelectionState(newSel);
        }
      }
      triggerAutosave();
      return;
    }

    // ── Faz 7: canvas.dil.{field} ─────────────────────────────────────────────
    if (fieldId.startsWith('canvas.dil.')) {
      const field = fieldId.split('.')[2];
      const newSection = { ...(localEditsRef.current.canvas_dil || {}), [field]: value };
      const newEdits = { ...localEditsRef.current, canvas_dil: newSection };
      localEditsRef.current = newEdits;
      setLocalEdits(newEdits);
      triggerAutosave();
      return;
    }

    // ── Faz 7: egitim.sertifikalar.{idx} ─────────────────────────────────────
    if (fieldId.startsWith('egitim.sertifikalar.')) {
      const sertIdx = parseInt(fieldId.split('.')[2], 10);
      if (!isNaN(sertIdx)) {
        const newList = canvasSertifikalarRef.current.map((s, i) => i === sertIdx ? value : s);
        canvasSertifikalarRef.current = newList;
        setCanvasSertifikalar(newList);
        triggerAutosave();
        return;
      }
    }

    // ── Existing field saves (legacy fieldIds) ────────────────────────────────
    let newEdits = { ...localEditsRef.current };
    if (fieldId === 'edit-unvan') {
      newEdits = { ...newEdits, unvan: value };
    } else if (fieldId === 'edit-hakkimda') {
      newEdits = { ...newEdits, hakkimda: value };
      // Auto-include hakkimda section when content is saved for the first time
      if (value) {
        const curSel = selectionStateRef.current || {};
        if (!curSel.includedSections?.hakkimda) {
          const newSel = {
            ...curSel,
            includedSections: { ...(curSel.includedSections || {}), hakkimda: true },
          };
          selectionStateRef.current = newSel;
          setSelectionState(newSel);
        }
      }
    } else if (fieldId === 'kisisel_form') {
      newEdits = { ...newEdits, kisisel_form: value };
    } else if (fieldId === 'egitim_form') {
      newEdits = { ...newEdits, egitim_form: value };
    } else if (fieldId === 'dil_form') {
      newEdits = { ...newEdits, dil_form: value };
    } else if (fieldId === 'hakkimda_form') {
      newEdits = { ...newEdits, unvan: value.unvan, hakkimda: value.hakkimda };
    } else if (fieldId === 'is_deneyimi_form') {
      isDeneyimleriRef.current = value;
      setIsDeneyimleri(value);
    } else if (fieldId === 'canvas_sertifikalar') {
      // MobileEditSheet egitim sertifikalar save — canvasSertifikalar tek kaynak
      canvasSertifikalarRef.current = value;
      setCanvasSertifikalar(value);
      triggerAutosave();
      return;
    } else if (fieldId === 'canvas_diller') {
      // Faz 6 MobileEditSheet dil list save — update canvasDilDiller state
      canvasDilDillerRef.current = value;
      setCanvasDilDiller(value);
      triggerAutosave();
      return;
    } else if (fieldId === 'application_info') {
      // Faz 7 ApplicationInfoDialog save
      newEdits = { ...newEdits, application_info: value };
    }
    setLocalEdits(newEdits);
    localEditsRef.current = newEdits;
    triggerAutosave();
  }, [triggerAutosave]);

  // ─── Canvas CRUD — İş Deneyimi ────────────────────────────────────────────
  const handleCanvasDeneyimAdd = useCallback(() => {
    const newEntry = { pozisyon: '', sirket: '', baslangic: '', bitis: '', devam_ediyor: false, maddeler: [''] };
    const newList = [...isDeneyimleriRef.current, newEntry];
    const newIdx = newList.length - 1;
    isDeneyimleriRef.current = newList;
    setIsDeneyimleri(newList);
    // New record defaults to included in CV; also auto-include section if it was excluded
    const curSel = selectionStateRef.current || {};
    const newSel = {
      ...curSel,
      isDeneyimleriInclude: { ...(curSel.isDeneyimleriInclude || {}), [newIdx]: true },
      includedSections: { ...(curSel.includedSections || {}), is_deneyimleri: true },
    };
    selectionStateRef.current = newSel;
    setSelectionState(newSel);
    triggerAutosave();
    // Bug #4 fix: correct attribute selector for InlineField read spans
    requestAnimationFrame(() => {
      const btn = document.querySelector(`[data-section="is_deneyimleri"] [role="button"]`);
      if (btn) btn.click();
    });
  }, [triggerAutosave]);

  const handleCanvasDeneyimDelete = useCallback((origIdx) => {
    const newList = isDeneyimleriRef.current.filter((_, i) => i !== origIdx);
    isDeneyimleriRef.current = newList;
    setIsDeneyimleri(newList);
    // Renumber selectionState include map
    const curSel = selectionStateRef.current || {};
    const oldInclude = curSel.isDeneyimleriInclude || {};
    const newInclude = {};
    Object.entries(oldInclude).forEach(([k, v]) => {
      const ki = parseInt(k, 10);
      if (ki < origIdx) newInclude[ki] = v;
      else if (ki > origIdx) newInclude[ki - 1] = v;
    });
    const newSel = { ...curSel, isDeneyimleriInclude: newInclude };
    selectionStateRef.current = newSel;
    setSelectionState(newSel);
    triggerAutosave();
  }, [triggerAutosave]);

  const handleCanvasDeneyimMaddeAdd = useCallback((entryIdx) => {
    const newList = isDeneyimleriRef.current.map((entry, i) => {
      if (i !== entryIdx) return entry;
      if ((entry.maddeler || []).length >= 4) return entry;
      return { ...entry, maddeler: [...(entry.maddeler || []), ''] };
    });
    isDeneyimleriRef.current = newList;
    setIsDeneyimleri(newList);
    triggerAutosave();
  }, [triggerAutosave]);

  const handleCanvasDeneyimMaddeDelete = useCallback((entryIdx, maddeIdx) => {
    const newList = isDeneyimleriRef.current.map((entry, i) => {
      if (i !== entryIdx) return entry;
      return { ...entry, maddeler: (entry.maddeler || []).filter((_, mi) => mi !== maddeIdx) };
    });
    isDeneyimleriRef.current = newList;
    setIsDeneyimleri(newList);
    triggerAutosave();
  }, [triggerAutosave]);

  // ─── Canvas CRUD — Dil ───────────────────────────────────────────────────
  const handleCanvasDilAdd = useCallback(() => {
    const newEntry = { dil: '', seviye: '' };
    const newList = [...canvasDilDillerRef.current, newEntry];
    const newIdx = newList.length - 1;
    canvasDilDillerRef.current = newList;
    setCanvasDilDiller(newList);
    // New dil defaults to included; also auto-include section if it was excluded
    const curSel = selectionStateRef.current || {};
    const newSel = {
      ...curSel,
      dillerInclude: { ...(curSel.dillerInclude || {}), [newIdx]: true },
      includedSections: { ...(curSel.includedSections || {}), dil: true },
    };
    selectionStateRef.current = newSel;
    setSelectionState(newSel);
    triggerAutosave();
  }, [triggerAutosave]);

  const handleCanvasDilDelete = useCallback((origIdx) => {
    const newList = canvasDilDillerRef.current.filter((_, i) => i !== origIdx);
    canvasDilDillerRef.current = newList;
    setCanvasDilDiller(newList);
    // Renumber dillerInclude
    const curSel = selectionStateRef.current || {};
    const oldInclude = curSel.dillerInclude || {};
    const newInclude = {};
    Object.entries(oldInclude).forEach(([k, v]) => {
      const ki = parseInt(k, 10);
      if (ki < origIdx) newInclude[ki] = v;
      else if (ki > origIdx) newInclude[ki - 1] = v;
    });
    const newSel = { ...curSel, dillerInclude: newInclude };
    selectionStateRef.current = newSel;
    setSelectionState(newSel);
    triggerAutosave();
  }, [triggerAutosave]);

  // ─── Canvas CRUD — Sertifikalar (Faz 7) ──────────────────────────────────────
  const handleCanvasSertifikaAdd = useCallback(() => {
    const newList = [...canvasSertifikalarRef.current, ''];
    canvasSertifikalarRef.current = newList;
    setCanvasSertifikalar(newList);
    triggerAutosave();
  }, [triggerAutosave]);

  const handleCanvasSertifikaDelete = useCallback((idx) => {
    const newList = canvasSertifikalarRef.current.filter((_, i) => i !== idx);
    canvasSertifikalarRef.current = newList;
    setCanvasSertifikalar(newList);
    triggerAutosave();
  }, [triggerAutosave]);

  // ─── Navigation ─────────────────────────────────────────────────────────────

  const saveCurrentStepData = useCallback(
    (data) => {
      formDataRef.current[state.currentStep] = data;
    },
    [state.currentStep]
  );

  const doNavigateNext = (fromStep) => {
    dispatch({ type: 'MARK_COMPLETED', step: fromStep });
    dispatch({ type: 'SET_STEP', step: fromStep + 1 });
  };

  const handleNext = async () => {
    // Welcome adımı (0) — KVKK onayı şart, form validasyonu yok
    if (state.currentStep === 0) {
      if (!state.kvkkConsented) return;
      doNavigateNext(0);
      return;
    }
    // Edit adımı (1) — açık InlineField varsa unsaved-changes modal göster
    if (state.currentStep === 1) {
      if (hasActiveEditRef.current) {
        setPendingNavStep(2);
        setEditUnsavedModal(true);
        return;
      }
      doNavigateNext(1);
      return;
    }
    // Save adımı (2) — ileri gidilecek adım yok; gönderme SaveStep içinde yapılır
  };

  const handleSkip = () => {
    dispatch({ type: 'MARK_SKIPPED', step: state.currentStep });
    if (state.currentStep < 2) {
      dispatch({ type: 'SET_STEP', step: state.currentStep + 1 });
    }
  };

  const handleBack = () => {
    const data = form.getValues();
    saveCurrentStepData(data);
    if (state.currentStep > 0) {
      dispatch({ type: 'SET_STEP', step: state.currentStep - 1 });
    } else {
      navigate('/programs/kabin-memurlugu/interview');
    }
  };

  const handleSubmit = async () => {
    // Guard: KVKK onayı olmadan gönderim yapılamaz
    if (!state.kvkkConsented) {
      toast.error(t('cv_wizard.welcome.kvkk_title'));
      return;
    }
    dispatch({ type: 'MARK_COMPLETED', step: state.currentStep });

    // Europass wizard: use buildEuropassBody
    const body = buildEuropassBody(
      cvData, localEditsRef.current, isDeneyimleriRef.current, selectionStateRef.current,
      state.kvkkConsented, fotoUrl, templateIdRef.current, canvasDilDillerRef.current,
      canvasSertifikalarRef.current
    );
    try {
      await submitMutation.mutateAsync(body);
      dispatch({ type: 'SET_SUBMITTED' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;

      if (status === 401) {
        // AlertDialog pattern from spec — navigate to login
        navigate('/login');
        return;
      }

      const msg = detail
        ? (typeof detail === 'object'
          ? (detail.message_tr || detail.message_en || t('cv.error.submit_failed'))
          : String(detail))
        : t('cv.error.submit_failed');
      toast.error(msg);
    }
  };

  // handleKVKKAccept / handleKVKKReject: Faz 2'de modal kaldırıldı.
  // KVKK artık WelcomeStep inline card + handleKVKKInline üzerinden yönetilir.

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (isLoading || !seeded) {
    return (
      <div className="min-h-screen bg-[var(--bg-app)] w-full p-4 md:p-6">
        <WizardSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-[var(--bg-app)] p-4 md:p-6">
        <CabinErrorState
          onRetry={refetch}
          backPath="/programs/kabin-memurlugu/interview"
          testId="cv-wizard-error"
        />
      </div>
    );
  }

  // After submitting, OR if CV already has a reviewable status (not draft) → show status screen
  const reviewableStatuses = ['pending_review', 'in_review', 'approved', 'changes_requested'];
  const cvStatus = cvData?.status;
  const showStatusScreen = state.submitted || (seeded && cvData && reviewableStatuses.includes(cvStatus));

  if (showStatusScreen && !forceEdit) {
    // After submit refetch may still show old draft; use submitted status directly when state.submitted
    const displayData = state.submitted
      ? { ...cvData, status: cvData?.status || 'pending_review' }
      : cvData;
    return (
      <div className="min-h-screen bg-[var(--bg-app)] w-full p-4 md:p-6">
        <CVStatusScreen
          cvData={displayData}
          t={t}
          lang={lang}
          reduced={reduced}
          onEditCV={() => setForceEdit(true)}
        />
      </div>
    );
  }

  const StepIcon = STEP_ICONS[state.currentStep];
  const isLastStep = state.currentStep === 2;
  const isFirstStep = state.currentStep === 0;

  // Stepper altındaki kısa açıklamalar
  const STEP_DESCS = [
    t('cv_wizard.welcome.subtitle'),
    t('cv_wizard.edit.subtitle'),
    t('cv_wizard.save.subtitle'),
  ];

  return (
    <>
      {/* Unsaved changes modal for Edit step */}
      {editUnsavedModal && (
        <Dialog open={editUnsavedModal} onOpenChange={(v) => { if (!v) setEditUnsavedModal(false); }}>
          <DialogContent className="max-w-sm rounded-2xl border-2 border-gray-200 shadow-[4px_4px_0_#E5E7EB]">
            <DialogHeader>
              <DialogTitle className="font-display text-lg font-black text-gray-900">
                {t('cv_wizard.edit.unsaved_title')}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-600 leading-relaxed mt-2">
              {t('cv_wizard.edit.unsaved_body')}
            </p>
            <div className="flex gap-3 mt-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                type="button"
                onClick={() => setEditUnsavedModal(false)}
                className="flex-1 py-2.5 px-4 bg-white text-gray-700 font-bold rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-colors min-h-[44px]"
              >
                {t('cv_wizard.edit.unsaved_back')}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                type="button"
                onClick={() => {
                  setEditUnsavedModal(false);
                  if (pendingNavStep !== null) {
                    dispatch({ type: 'MARK_COMPLETED', step: 1 });
                    dispatch({ type: 'SET_STEP', step: pendingNavStep });
                    setPendingNavStep(null);
                  }
                }}
                className="flex-1 py-2.5 px-4 bg-rose-600 text-white font-bold rounded-xl border-2 border-rose-700 shadow-[2px_2px_0_#be123c] hover:bg-rose-700 transition-colors min-h-[44px]"
              >
                {t('cv_wizard.edit.unsaved_continue')}
              </motion.button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <div
        className="min-h-screen bg-[var(--bg-app)] w-full"
        data-testid="cv-wizard-page"
      >
        {/* Hero Header */}
        <div className="bg-gradient-to-br from-sky-600 to-blue-800 text-white py-8 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-white/20 border-2 border-white/30 flex items-center justify-center">
                <ClipboardList size={20} aria-hidden="true" />
              </div>
              <div>
                <h1 className="font-display text-2xl font-black leading-tight">
                  {t('cv.wizard.title')}
                </h1>
                <p className="text-sky-200 text-sm mt-0.5">{t('cv.wizard.subtitle')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 py-6 space-y-5">
          {/* Progress Bar — stepper makul genişlikte ortalı kalır */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-white border-2 border-gray-100 rounded-2xl p-4 shadow-sm">
              <WizardSteps
                steps={STEPS}
                current={state.currentStep}
                skipped={state.skipped}
                completed={state.completed}
              />

              {/* Autosave indicator */}
              <div className="flex justify-end mt-3 min-h-[18px]">
                <AutosaveIndicator status={state.autosaveStatus} t={t} />
              </div>
            </div>
          </div>

          {/* Step Content Card — full-width */}
          <AnimatePresence mode="wait">
            <motion.div
              key={state.currentStep}
              initial={reduced ? { opacity: 1 } : { opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={reduced ? { opacity: 0 } : { opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="bg-white border-2 border-gray-100 rounded-2xl shadow-sm"
            >
              {/* Step Header */}
              <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-gray-100">
                <div className="w-10 h-10 rounded-xl bg-sky-50 border-2 border-sky-200 flex items-center justify-center flex-shrink-0">
                  <StepIcon size={20} className="text-sky-600" aria-hidden="true" />
                </div>
                <div>
                  <h2
                    ref={stepHeadingRef}
                    tabIndex={-1}
                    className="font-display text-lg font-black text-gray-900 leading-tight focus:outline-none"
                  >
                    {STEP_LABELS[state.currentStep]}
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {STEP_DESCS[state.currentStep]}
                  </p>
                </div>
              </div>

              {/* Step Content Body */}
              <div className="px-5 py-5">
                {/* Adım 0: Welcome — profil özet + KVKK inline */}
                {state.currentStep === 0 && (
                  <WelcomeStep
                    cvData={cvData}
                    kvkkConsented={state.kvkkConsented}
                    onKvkkChange={handleKVKKInline}
                    t={t}
                    onGoToEdit={() => dispatch({ type: 'SET_STEP', step: 1 })}
                  />
                )}
                {/* Adım 1: Edit — CVCanvas inline-edit */}
                {state.currentStep === 1 && (
                  <EditStep
                    cvData={cvData}
                    isDeneyimleri={isDeneyimleri}
                    selectionState={selectionState}
                    localEdits={localEdits}
                    onFieldSave={handleFieldSave}
                    onIsDeneyimleriChange={(val) => {
                      isDeneyimleriRef.current = val;
                      setIsDeneyimleri(val);
                      triggerAutosave();
                    }}
                    t={t}
                    autosaveStatus={state.autosaveStatus}
                    onHasActiveEditChange={handleEditActiveChange}
                    fotoBlobUrl={fotoBlobUrl}
                    canvasDilDiller={canvasDilDiller}
                    onDeneyimAdd={handleCanvasDeneyimAdd}
                    onDeneyimDelete={handleCanvasDeneyimDelete}
                    onDeneyimMaddeAdd={handleCanvasDeneyimMaddeAdd}
                    onDeneyimMaddeDelete={handleCanvasDeneyimMaddeDelete}
                    onDilAdd={handleCanvasDilAdd}
                    onDilDelete={handleCanvasDilDelete}
                    templateId={templateId}
                    onTemplateChange={handleTemplateChange}
                    canvasSertifikalar={canvasSertifikalar}
                    onSertifikaAdd={handleCanvasSertifikaAdd}
                    onSertifikaDelete={handleCanvasSertifikaDelete}
                    onSectionToggle={handleCanvasSectionToggle}
                    onFotoUpload={handleCanvasFotoUpload}
                    onFotoDelete={handleCanvasFotoDelete}
                    fotoUploading={fotoUploading}
                    fotoDeleting={fotoDeleting}
                  />
                )}
                {/* Adım 2: Save — özet + PDF + gönder */}
                {state.currentStep === 2 && (
                  <SaveStep
                    cvData={cvData}
                    selectionState={selectionState}
                    localEdits={localEdits}
                    isDeneyimleri={isDeneyimleri}
                    templateId={templateId}
                    t={t}
                    lang={lang}
                    onSubmit={handleSubmit}
                    submitting={submitMutation.isPending}
                    cvStatus={cvData?.status}
                    canvasDilDiller={canvasDilDiller}
                  />
                )}
              </div>

              {/* Navigation Footer */}
              <div className="flex items-center justify-between gap-3 px-5 pb-5 pt-3 border-t border-gray-100">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  type="button"
                  onClick={handleBack}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 font-bold text-sm hover:border-gray-300 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400 min-h-[44px]"
                >
                  <ArrowLeft size={16} aria-hidden="true" />
                  {t('cv.nav.back')}
                </motion.button>

                <div className="flex items-center gap-2">
                  {/* Skip: Welcome ve Save adımlarında gösterilmez */}
                  {!isFirstStep && !isLastStep && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                      type="button"
                      onClick={handleSkip}
                      className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border-2 border-amber-200 text-amber-700 font-bold text-sm hover:border-amber-300 hover:bg-amber-50 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400 min-h-[44px]"
                    >
                      <Minus size={14} aria-hidden="true" />
                      {t('cv.nav.skip')}
                    </motion.button>
                  )}

                  {isLastStep ? null : isFirstStep ? (
                    /* Welcome adımı — "Başlayalım" CTA, KVKK onayı olmadan disabled */
                    <motion.button
                      whileHover={{ scale: state.kvkkConsented ? 1.02 : 1 }}
                      whileTap={{ scale: state.kvkkConsented ? 0.97 : 1 }}
                      type="button"
                      onClick={handleNext}
                      disabled={!state.kvkkConsented}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-sky-600 text-white font-bold text-sm border-2 border-sky-700 shadow-[2px_2px_0_#0369a1] hover:bg-sky-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 min-h-[44px]"
                    >
                      {t('cv_wizard.welcome.cta')}
                      <ArrowRight size={16} aria-hidden="true" />
                    </motion.button>
                  ) : (
                    /* Diğer adımlar */
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                      type="button"
                      onClick={handleNext}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-sky-600 text-white font-bold text-sm border-2 border-sky-700 shadow-[2px_2px_0_#0369a1] hover:bg-sky-700 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 min-h-[44px]"
                    >
                      {t('cv.nav.next')}
                      <ArrowRight size={16} aria-hidden="true" />
                    </motion.button>
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}
